<template>
  <div
    class="pos-page"
    :class="{
      'pos-page--mobile-bar': mostrarBarraMobile,
      'pos-page--fs': pantallaCompleta,
    }"
  >
    <NavbarMain />

    <div v-if="deviceBlocked" class="container po-dash-container py-5">
      <div class="alert alert-danger border-0 shadow-sm rounded-3">
        <h2 class="h5 mb-2">Dispositivo bloqueado</h2>
        <p class="small mb-3">
          Este dispositivo fue revocado o reemplazado por el administrador.
          Existen {{ offline.pending }} venta(s) pendientes de sincronizar.
        </p>
        <div class="d-flex flex-wrap gap-2">
          <button
            type="button"
            class="btn btn-outline-dark btn-sm rounded-pill"
            @click="exportOfflineBackup"
          >
            Exportar respaldo
          </button>
          <button
            v-if="offline.online"
            type="button"
            class="btn btn-primary btn-sm rounded-pill"
            @click="onReEnrollDeviceClick"
          >
            Re-enrolar
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="offline.upgradeRequired"
      class="container po-dash-container pt-2 pb-0"
    >
      <div class="alert alert-danger py-2 mb-0 small">
        <strong>POS desactualizado.</strong> Actualizá la aplicación antes de
        crear nuevas ventas. Las ventas pendientes permanecen guardadas.
      </div>
    </div>

    <div
      v-if="offline.timeWarning"
      class="container po-dash-container pt-2 pb-0"
    >
      <div class="alert alert-warning py-2 mb-0 small">
        <strong>Revisá la fecha y hora de esta PC.</strong> Hay más de cinco
        minutos de diferencia con el servidor.
      </div>
    </div>

    <div
      v-if="offline.online && offline.deviceCertWarning"
      class="container po-dash-container pt-2 pb-0"
    >
      <div class="alert alert-warning py-2 mb-0 small">
        <strong>Certificado del dispositivo POS próximo a vencer.</strong>
        Conectate a internet para renovarlo automáticamente.
      </div>
    </div>

    <div
      v-if="offline.online && offline.conflicts.length"
      class="container po-dash-container pt-2 pb-0"
    >
      <div class="alert alert-warning mb-0">
        <strong class="small">Ventas que requieren revisión</strong>
        <div
          v-for="item in offline.conflicts"
          :key="item.client_mutation_id"
          class="small border-top mt-2 pt-2"
        >
          <span
            >{{ item.ticket_folio || item.client_mutation_id }} ·
            {{ estadoConflicto(item) }}</span
          >
          <div
            v-if="item.sync_status === 'caja_mismatch'"
            class="d-flex flex-wrap gap-2 mt-2"
          >
            <button
              type="button"
              class="btn btn-sm btn-dark"
              @click="adjuntarConflictoCaja(item)"
            >
              Adjuntar a caja actual
            </button>
            <button
              type="button"
              class="btn btn-sm btn-outline-danger"
              @click="descartarConflicto(item)"
            >
              Descartar
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="!deviceBlocked && cajaRequerida && !cajaAbierta && offline.online"
      class="container po-dash-container pt-2 pb-0"
    >
      <div
        class="alert alert-warning py-2 mb-0 d-flex flex-wrap align-items-center justify-content-between gap-2"
      >
        <span class="small mb-0">{{ cajaBanner }}</span>
        <button
          type="button"
          class="btn btn-sm btn-dark rounded-pill"
          @click="abrirModalCaja"
        >
          Abrir caja
        </button>
      </div>
    </div>
    <div
      v-else-if="
        !deviceBlocked && cajaRequerida && !cajaAbierta && !offline.online
      "
      class="container po-dash-container pt-2 pb-0"
    >
      <div
        class="alert alert-warning py-2 mb-0 d-flex flex-wrap align-items-center justify-content-between gap-2"
      >
        <span class="small mb-0"
          >{{ cajaBanner }} Podés abrirla offline desde acá o en Caja.</span
        >
        <button
          type="button"
          class="btn btn-sm btn-dark rounded-pill"
          @click="abrirModalCaja"
        >
          Abrir caja
        </button>
      </div>
    </div>

    <div
      v-if="!deviceBlocked"
      class="pos-shell"
      :class="{ 'pos-shell--mobile': esMobile }"
    >
      <!-- Columna izquierda: escáner + catálogo -->
      <section v-show="mostrarCatalogo" class="pos-catalog">
        <div class="pos-scan">
          <div class="pos-scan__field">
            <i class="bi bi-upc-scan pos-scan__ico" aria-hidden="true"></i>
            <input
              ref="scanInput"
              v-model="scan"
              class="form-control form-control-lg pos-scan__input"
              placeholder="Escaneá o buscá por producto o categoría…"
              autocomplete="off"
              @keyup.enter="procesarScan"
            />
            <button
              v-if="scan"
              type="button"
              class="btn btn-link pos-scan__clear"
              @click="
                scan = '';
                enfocarScan();
              "
            >
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
          <button
            v-if="!esMobile"
            type="button"
            class="btn btn-outline-secondary pos-scan__fs"
            :title="
              pantallaCompleta
                ? 'Salir de pantalla completa (Esc)'
                : 'Pantalla completa'
            "
            :aria-pressed="pantallaCompleta"
            @click="togglePantallaCompleta"
          >
            <i
              :class="
                pantallaCompleta
                  ? 'bi bi-fullscreen-exit'
                  : 'bi bi-arrows-fullscreen'
              "
              aria-hidden="true"
            ></i>
            <span class="visually-hidden">{{
              pantallaCompleta
                ? "Salir de pantalla completa"
                : "Pantalla completa"
            }}</span>
          </button>
        </div>

        <div class="pos-cats">
          <button
            type="button"
            class="btn btn-sm rounded-pill btn-outline-dark pos-libre-btn"
            title="Cobrar un monto que no está en el catálogo (F6)"
            @click="abrirModalLibre"
          >
            <i class="bi bi-plus-circle me-1" aria-hidden="true"></i>Cobro libre
            <kbd class="pos-kbd pos-kbd--muted d-none d-lg-inline ms-1">F6</kbd>
          </button>
        </div>

        <div v-if="codigoInexistente" class="pos-inexistente">
          <div class="pos-inexistente__info">
            <i class="bi bi-exclamation-circle" aria-hidden="true"></i>
            <div class="min-w-0">
              <strong>Producto inexistente</strong>
              <p class="mb-0">
                No hay un producto con el código
                <span class="font-monospace">{{ codigoInexistente }}</span>
              </p>
            </div>
          </div>
          <button
            type="button"
            class="btn btn-primary btn-sm rounded-pill px-3"
            @click="abrirModalNuevoProducto"
          >
            <i class="bi bi-plus-lg me-1" aria-hidden="true"></i>
            Agregarlo
          </button>
        </div>

        <div class="pos-grid">
          <div
            v-for="p in productosFiltrados"
            :key="String(p.producto_id)"
            class="pos-prod"
            :class="{
              'pos-prod--consultar': esConsultarPrecio(p),
              'pos-prod--sinstock': sinStock(p),
              'pos-prod--pinned': productoFijado(p),
            }"
            role="button"
            :tabindex="sinStock(p) ? -1 : 0"
            @click="onProdCatalogClick(p, $event)"
            @keydown.enter.prevent="onProdCatalogClick(p, $event)"
            @keydown.space.prevent="onProdCatalogClick(p, $event)"
          >
            <span class="pos-prod__head">
              <span class="pos-prod__name">{{ p.producto_nombre }}</span>
              <button
                type="button"
                class="pos-prod__pin"
                :class="{ 'is-on': productoFijado(p) }"
                :title="productoFijado(p) ? 'Quitar de fijos' : 'Fijar al inicio'"
                :aria-label="productoFijado(p) ? 'Quitar de fijos' : 'Fijar al inicio'"
                @click.stop="toggleFijarProducto(p)"
              >
                <i class="bi" :class="productoFijado(p) ? 'bi-pin-angle-fill' : 'bi-pin-angle'" aria-hidden="true"></i>
              </button>
            </span>
            <span
              class="pos-prod__price"
              :class="{ 'pos-prod__price--consultar': esConsultarPrecio(p) }"
            >
              <template v-if="esConsultarPrecio(p)">Consultar precio</template>
              <template v-else>${{ money(p.producto_precio) }}</template>
            </span>
            <span
              class="pos-prod__stock"
              :class="{ 'text-danger': sinStock(p) }"
            >
              {{
                Number(p.producto_stock) === -1
                  ? "Sin límite"
                  : "Stock " + p.producto_stock
              }}
            </span>
          </div>
          <p
            v-if="!productosFiltrados.length && !codigoInexistente"
            class="text-muted small text-center w-100 py-4 mb-0"
          >
            No hay productos con ese criterio.
          </p>
        </div>
      </section>

            <!-- Columna derecha: carrito o cobro (misma columna, sin cambiar de pantalla) -->
            <aside v-show="mostrarVenta" class="pos-cart" :class="{ 'pos-cart--checkout': modoCobro }">
                <div class="pos-cart__head">
                    <template v-if="!modoCobro">
                        <div class="d-flex align-items-center gap-2 flex-wrap min-w-0">
                            <button
                                v-if="esMobile"
                                type="button"
                                class="btn btn-sm btn-link text-decoration-none p-0 pos-cart__back"
                                @click="irACatalogoMobile"
                            >
                                <i class="bi bi-arrow-left me-1" aria-hidden="true"></i>Productos
                            </button>
                            <h1 class="h6 mb-0">Venta actual</h1>
                            <span
                                v-if="cajaRequerida && cajaAbierta"
                                class="badge rounded-pill pos-caja-badge"
                            >
                                {{ cajaEtiqueta }}
                            </span>
                        </div>
                        <button v-if="lineas.length" class="btn btn-sm btn-link text-danger p-0" @click="confirmarVaciar">Vaciar</button>
                    </template>
                    <template v-else>
                        <button type="button" class="btn btn-sm btn-link text-decoration-none p-0 pos-cart__back" @click="cerrarCobro">
                            <i class="bi bi-arrow-left me-1" aria-hidden="true"></i>Volver
                        </button>
                        <div class="text-center flex-grow-1 min-w-0">
                            <div class="small text-muted mb-0">Cobrar</div>
                            <div class="pos-cart__checkout-total">${{ money(total) }}</div>
                        </div>
                        <span class="pos-cart__head-spacer" aria-hidden="true"></span>
                    </template>
                </div>

                <div class="pos-cart__body">
                    <template v-if="!modoCobro">
                        <div v-if="!lineas.length" class="pos-cart__empty">
                            <span class="display-6">🧾</span>
                            <p class="small text-muted mb-0">Escaneá o tocá productos para empezar.</p>
                        </div>

                        <ul v-else class="pos-lines">
                            <li v-for="row in lineas" :key="row.key" class="pos-line">
                                <div class="pos-line__info">
                                    <div class="pos-line__name">
                                        <span class="pos-line__name-text">{{ row.p.producto_nombre }}</span>
                                        <span v-if="row.libre" class="pos-line__libre">Libre</span>
                                    </div>
                                    <div class="pos-line__sub">${{ money(row.precioUnitario) }} c/u</div>
                                </div>
                                <div class="pos-line__qty">
                                    <button type="button" class="btn btn-sm btn-outline-secondary pos-line__qty-btn" @click="cambiarCantidadLinea(row, -1)">−</button>
                                    <input
                                        type="number"
                                        class="form-control form-control-sm pos-line__qty-input"
                                        :value="row.q"
                                        min="0"
                                        :max="stockMaxAttr(row)"
                                        inputmode="numeric"
                                        aria-label="Cantidad"
                                        @change="onCantidadLineaInput(row, $event)"
                                    />
                                    <button
                                        type="button"
                                        class="btn btn-sm btn-outline-primary pos-line__qty-btn"
                                        :disabled="!puedeSumarLinea(row)"
                                        @click="cambiarCantidadLinea(row, 1)"
                                    >+</button>
                                </div>
                                <div class="pos-line__total">${{ money(row.subtotal) }}</div>
                                <button type="button" class="btn btn-sm btn-link text-danger pos-line__del" @click="quitarLinea(row)">
                                    <i class="bi bi-trash"></i>
                                </button>
                            </li>
                        </ul>
                    </template>

                    <div v-else class="pos-checkout-panel" :class="{ 'pos-checkout-panel--qr': esMedioQr }">
                        <template v-if="!esMedioQr">
                        <label class="form-label small fw-semibold mb-1">Medio de pago <span class="text-muted fw-normal">(1–{{ mediosVisibles.length }})</span></label>
                        <div class="pos-pay-methods pos-checkout-methods">
                            <button
                                v-for="(m, idx) in mediosVisibles"
                                :key="m.id"
                                type="button"
                                class="pos-pay-btn"
                                :class="{ active: medioPago === m.id }"
                                :disabled="medioPagoDeshabilitado(m)"
                                :title="tituloMedioPago(m)"
                                @click="seleccionarMedioPago(m)"
                            >
                                <i :class="m.icon" aria-hidden="true"></i>
                                <span>{{ m.label }}</span>
                                <kbd class="pos-kbd pos-kbd--dark">{{ idx + 1 }}</kbd>
                            </button>
                        </div>
                        </template>

                        <div v-if="medioPago === 'Efectivo'" class="pos-checkout-cash">
                            <div class="pos-checkout-cash__row">
                                <div class="pos-checkout-cash__paga">
                                    <label class="form-label small fw-semibold mb-1">Paga con</label>
                                    <input
                                        ref="recibidoInput"
                                        v-model.number="recibido"
                                        type="number"
                                        min="0"
                                        class="form-control form-control-sm"
                                        placeholder="0"
                                    />
                                </div>
                                <div class="pos-checkout-cash__vuelto">
                                    <span class="small text-muted">Vuelto</span>
                                    <span class="fw-bold" :class="vuelto < 0 ? 'text-danger' : 'text-success'">${{ money(Math.max(0, vuelto)) }}</span>
                                </div>
                            </div>
                            <div class="pos-checkout-bills">
                                <button v-for="b in billetes" :key="b" type="button" class="btn btn-sm btn-outline-secondary rounded-pill" @click="recibido = b">${{ money(b) }}</button>
                                <button type="button" class="btn btn-sm btn-outline-secondary rounded-pill" @click="recibido = total">Justo</button>
                            </div>
                        </div>

                        <div v-if="medioPago === 'Transferencia'" class="pos-checkout-transfer">
                            <p class="small fw-semibold mb-2">Datos para transferir</p>
                            <div v-if="tieneDatosTransferencia" class="pos-transfer-box">
                                <div v-if="negocio.pagoAlias">
                                    <span>Alias</span>
                                    <strong>{{ negocio.pagoAlias }}</strong>
                                </div>
                                <div v-if="negocio.pagoCvu">
                                    <span>CVU / CBU</span>
                                    <strong>{{ negocio.pagoCvu }}</strong>
                                </div>
                                <div v-if="negocio.pagoTitular">
                                    <span>Titular</span>
                                    <strong>{{ negocio.pagoTitular }}</strong>
                                </div>
                                <div v-if="negocio.pagoBanco">
                                    <span>Banco</span>
                                    <strong>{{ negocio.pagoBanco }}</strong>
                                </div>
                            </div>
                            <p v-else class="small text-muted mb-0">
                                No hay datos de transferencia cargados. Configuralos en Modificar perfil.
                            </p>
                        </div>

                        <div v-if="esMedioQr" class="pos-checkout-qr mb-0">
                            <div v-if="!mpVinculado && !mpQr.cargandoEstado" class="alert alert-warning py-2 px-3 small mb-2">
                                Este negocio no tiene Mercado Pago vinculado.
                                <router-link to="/u/modificar">Vincular en Perfil</router-link>
                            </div>
                            <div v-else-if="mpQr.error" class="alert alert-danger py-2 px-3 small mb-2">
                                {{ mpQr.error }}
                                <button type="button" class="btn btn-link btn-sm p-0 ms-1" @click="generarQrMp">Reintentar</button>
                            </div>
                            <div v-else class="pos-qr-box text-center">
                                <div class="pos-qr-amount">${{ money(total) }}</div>
                                <div v-if="mpQr.generando" class="py-3 text-muted small">Generando cobro QR…</div>
                                <template v-else-if="mpQr.image">
                                    <img :src="mpQr.image" alt="QR Mercado Pago" class="pos-qr-img" width="280" height="280" />
                                    <p v-if="mpQr.pagado" class="small text-success mb-1 mt-2">
                                        <i class="bi bi-check-circle-fill me-1" aria-hidden="true"></i>Pago recibido
                                    </p>
                                    <p v-else class="small text-muted mb-1 mt-2">
                                        Escaneá con la app de Mercado Pago
                                        <span v-if="mpQr.pollando"> · esperando pago…</span>
                                    </p>
                                    <div class="pos-qr-actions">
                                        <button type="button" class="btn btn-sm btn-outline-secondary rounded-pill" :disabled="mpQr.generando" @click="generarQrMp">
                                            <i class="bi bi-arrow-clockwise me-1" aria-hidden="true"></i>Nuevo QR
                                        </button>
                                        <template v-if="!mpQr.pagado">
                                            <button type="button" class="btn btn-sm btn-outline-success rounded-pill" @click="marcarQrPagadoManual">
                                                <i class="bi bi-check-circle me-1" aria-hidden="true"></i>Ya pagó
                                            </button>
                                            <button type="button" class="btn btn-sm btn-outline-danger rounded-pill" @click="cancelarCobroQrManual">
                                                <i class="bi bi-x-circle me-1" aria-hidden="true"></i>Cancelar
                                            </button>
                                        </template>
                                    </div>
                                </template>
                                <button
                                    v-else
                                    type="button"
                                    class="btn btn-primary btn-sm rounded-pill"
                                    :disabled="!mpVinculado || mpQr.generando"
                                    @click="generarQrMp"
                                >
                                    <i class="bi bi-qr-code me-1" aria-hidden="true"></i>Generar cobro QR
                                </button>
                            </div>
                        </div>

                        <div v-if="esMedioPoint" class="pos-checkout-point">
                            <div v-if="!mpPointVinculado" class="alert alert-warning py-2 px-3 small mb-2">
                                Este negocio no tiene una terminal Point vinculada.
                                <router-link to="/u/modificar">Vincular en Perfil</router-link>
                            </div>
                            <div v-else-if="mpPoint.error" class="alert alert-danger py-2 px-3 small mb-2">
                                {{ mpPoint.error }}
                                <button type="button" class="btn btn-link btn-sm p-0 ms-1" @click="generarCobroPoint">Reintentar</button>
                            </div>
                            <div v-else class="pos-point-box text-center">
                                <div v-if="mpPoint.generando" class="py-3 text-muted small">Enviando cobro a la terminal…</div>
                                <template v-else-if="mpPoint.orderId">
                                    <i
                                        class="bi"
                                        :class="mpPoint.pagado ? 'bi-check-circle-fill text-success' : 'bi-credit-card-2-back text-primary'"
                                        style="font-size: 2.5rem"
                                        aria-hidden="true"
                                    ></i>
                                    <p class="small fw-semibold mb-1 mt-2">${{ money(total) }}</p>
                                    <p v-if="mpPoint.pagado" class="small text-success mb-1">
                                        <i class="bi bi-check-circle-fill me-1" aria-hidden="true"></i>Pago recibido
                                    </p>
                                    <p v-else class="small text-muted mb-1">
                                        Esperando que el cliente pague en la terminal…
                                        <span v-if="mpPoint.pollando" class="spinner-border spinner-border-sm ms-1" aria-hidden="true" />
                                    </p>
                                    <div class="d-flex flex-wrap justify-content-center gap-2 mt-2">
                                        <button
                                            v-if="!mpPoint.pagado"
                                            type="button"
                                            class="btn btn-sm btn-outline-danger rounded-pill"
                                            @click="cancelarCobroPointManual"
                                        >
                                            Cancelar
                                        </button>
                                    </div>
                                </template>
                                <button
                                    v-else
                                    type="button"
                                    class="btn btn-primary btn-sm rounded-pill"
                                    :disabled="!mpPointVinculado || mpPoint.generando"
                                    @click="generarCobroPoint"
                                >
                                    Enviar cobro a la terminal
                                </button>
                            </div>
                        </div>

                        <div v-if="!esMedioQr" class="pos-checkout-extra">
                            <div class="position-relative" ref="clienteWrap">
                                <label class="form-label small fw-semibold mb-1">Cliente guardado</label>
                                <input
                                    v-model="clienteBusqueda"
                                    type="text"
                                    class="form-control form-control-sm"
                                    placeholder="Nombre o teléfono…"
                                    autocomplete="off"
                                    @focus="abrirClientesDd"
                                    @keydown.escape="cerrarClientesDd"
                                />
                                <ul
                                    v-show="clienteDdAbierto && clientesFiltrados.length"
                                    class="list-group position-absolute w-100 shadow-sm pos-cliente-dd"
                                >
                                    <li
                                        v-for="c in clientesFiltrados"
                                        :key="c.cliente_id"
                                        class="list-group-item list-group-item-action py-2 small"
                                        role="button"
                                        tabindex="0"
                                        @mousedown.prevent="seleccionarCliente(c)"
                                    >
                                        <span class="fw-medium">{{ c.nombre }}</span>
                                        <span v-if="c.telefono" class="text-muted"> · {{ c.telefono }}</span>
                                    </li>
                                </ul>
                                <p v-if="clienteId" class="small text-success mt-1 mb-0">
                                    {{ nombreClienteSeleccionado }}
                                    <button type="button" class="btn btn-link btn-sm p-0 ms-1 align-baseline" @click="limpiarCliente">Quitar</button>
                                </p>
                            </div>
                            <div>
                                <label class="form-label small fw-semibold mb-1">Nombre en la venta</label>
                                <input v-model.trim="clienteNombre" class="form-control form-control-sm" placeholder="Consumidor final" maxlength="255" />
                            </div>
                        </div>
                    </div>
                </div>

                <div class="pos-cart__foot" v-show="!modoCobro || !esMedioQr || mpQr.pagado">
                    <div v-if="!modoCobro" class="pos-total">
                        <span>Total</span>
                        <span class="pos-total__num">${{ money(total) }}</span>
                    </div>
                    <template v-else>
                        <div class="pos-cobro-opts">
                            <div class="form-check mb-0">
                                <input
                                    id="posImprimir"
                                    v-model="imprimir"
                                    class="form-check-input"
                                    type="checkbox"
                                />
                                <label class="form-check-label small" for="posImprimir">
                                    {{ facturarArca ? 'Imprimir comprobante fiscal' : 'Imprimir ticket' }}
                                </label>
                            </div>
                            <div v-if="puedeFacturarArca" class="form-check mb-0">
                                <input
                                    id="posFacturar"
                                    v-model="facturarArca"
                                    class="form-check-input"
                                    type="checkbox"
                                    :disabled="!offline.online"
                                />
                                <label class="form-check-label small" for="posFacturar">
                                    Facturar (ARCA)
                                </label>
                            </div>
                        </div>
                        <div
                            v-if="puedeFacturarArca && !offline.online"
                            class="form-text text-warning mb-0 mt-1"
                        >
                            Facturar requiere internet
                        </div>
                    </template>

                    <div class="pos-cart__pay-wrap">
                        <button
                            type="button"
                            class="btn btn-success btn-lg w-100 rounded-pill pos-cart__pay-btn"
                            :disabled="modoCobro ? cobrando || !puedeConfirmarCobro : !lineas.length"
                            @click="modoCobro ? cobrar() : abrirCobro()"
                        >
                            <span class="pos-cart__pay-btn-inner">
                                <span class="pos-cart__pay-btn-ico" aria-hidden="true">
                                    <span
                                        v-if="modoCobro && cobrando"
                                        class="spinner-border spinner-border-sm"
                                    ></span>
                                    <i
                                        v-else
                                        class="bi"
                                        :class="modoCobro ? 'bi-check2-circle' : 'bi-cash-coin'"
                                    ></i>
                                </span>
                                <span class="pos-cart__pay-btn-label">
                                    {{ modoCobro ? 'Confirmar cobro' : 'Cobrar' }}
                                </span>
                                <kbd class="pos-kbd pos-kbd--on-btn d-none d-lg-inline">
                                    {{ modoCobro ? 'Enter' : 'F2' }}
                                </kbd>
                            </span>
                        </button>
                        <p
                            v-if="!modoCobro"
                            class="pos-kbd-hint small text-muted mb-0 mt-2 text-center d-none d-lg-block"
                        >
                            <kbd class="pos-kbd pos-kbd--muted">F3</kbd> buscar · <kbd class="pos-kbd pos-kbd--muted">Esc</kbd> limpiar · <kbd class="pos-kbd pos-kbd--muted">F6</kbd> cobro libre · <kbd class="pos-kbd pos-kbd--muted">F4</kbd> vaciar
                        </p>
                        <p
                            v-else-if="modoCobro"
                            class="pos-kbd-hint small text-muted mb-0 mt-2 text-center d-none d-lg-block"
                        >
                            <kbd class="pos-kbd pos-kbd--muted">F2</kbd> o Enter · <kbd class="pos-kbd pos-kbd--muted">Esc</kbd> volver
                        </p>
                    </div>
                </div>
            </aside>
        </div>

    <div v-if="mostrarBarraMobile" class="pos-mobile-bar">
      <button type="button" class="pos-mobile-bar__btn" @click="irAVentaMobile">
        <span class="pos-mobile-bar__meta">
          <strong>{{ lineas.length }}</strong> producto(s) ·
          <strong>${{ money(total) }}</strong>
        </span>
        <span class="pos-mobile-bar__action"
          >Ver venta <i class="bi bi-chevron-up" aria-hidden="true"></i
        ></span>
      </button>
    </div>

    <!-- Modal abrir caja rápida -->
    <div
      class="modal fade"
      id="modalAbrirCajaPos"
      tabindex="-1"
      aria-hidden="true"
      ref="modalAbrirCajaEl"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content border-0 shadow-lg rounded-4">
          <form @submit.prevent="confirmarAbrirCajaRapida">
            <div class="modal-header border-0 pb-0">
              <div>
                <h2 class="modal-title fs-5 mb-1">Abrir caja</h2>
                <p class="text-muted small mb-0">
                  Primero abrí una caja para poder cobrar.
                </p>
              </div>
              <button
                type="button"
                class="btn-close"
                data-bs-dismiss="modal"
                aria-label="Cerrar"
              ></button>
            </div>
            <div class="modal-body">
              <label class="form-label">Monto inicial en efectivo</label>
              <input
                v-model.number="formAbrirCaja.monto_inicial"
                type="number"
                min="0"
                step="0.01"
                class="form-control"
                placeholder="0"
              />
              <label class="form-label mt-3">Observación (opcional)</label>
              <textarea
                v-model="formAbrirCaja.observacion"
                class="form-control"
                rows="2"
                maxlength="500"
              ></textarea>
            </div>
            <div class="modal-footer border-0 pt-0">
              <button
                type="button"
                class="btn btn-light rounded-pill"
                data-bs-dismiss="modal"
                :disabled="abriendoCaja"
              >
                Cancelar
              </button>
              <button
                type="submit"
                class="btn btn-primary rounded-pill px-4"
                :disabled="abriendoCaja"
              >
                <span
                  v-if="abriendoCaja"
                  class="spinner-border spinner-border-sm me-1"
                  aria-hidden="true"
                ></span>
                {{ abriendoCaja ? "Abriendo…" : "Abrir caja" }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Modal cobro libre -->
    <div
      class="modal fade"
      id="modalCobroLibrePos"
      tabindex="-1"
      aria-hidden="true"
      ref="modalLibreEl"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content border-0 shadow-lg rounded-4">
          <form @submit.prevent="confirmarCobroLibre">
            <div class="modal-header border-0 pb-0">
              <div>
                <h2 class="modal-title fs-5 mb-1">Cobro libre</h2>
                <p class="text-muted small mb-0">
                  Para algo que no está en el catálogo. No descuenta stock.
                </p>
              </div>
              <button
                type="button"
                class="btn-close"
                data-bs-dismiss="modal"
                aria-label="Cerrar"
              ></button>
            </div>
            <div class="modal-body vstack gap-3">
              <div>
                <label class="form-label" for="libreNombre">Descripción</label>
                <input
                  id="libreNombre"
                  ref="libreNombreInput"
                  v-model.trim="formLibre.nombre"
                  class="form-control"
                  placeholder="Ej: Servicio, envío, ajuste…"
                  maxlength="120"
                  autocomplete="off"
                />
              </div>
              <div class="row g-2">
                <div class="col-7">
                  <label class="form-label" for="librePrecio">Monto ($)</label>
                  <input
                    id="librePrecio"
                    v-model.number="formLibre.precio"
                    type="number"
                    min="0"
                    step="0.01"
                    class="form-control form-control-lg"
                    placeholder="0"
                    required
                  />
                </div>
                <div class="col-5">
                  <label class="form-label" for="libreCant">Cantidad</label>
                  <input
                    id="libreCant"
                    v-model.number="formLibre.cantidad"
                    type="number"
                    min="1"
                    step="1"
                    class="form-control form-control-lg"
                  />
                </div>
              </div>
            </div>
            <div class="modal-footer border-0 pt-0">
              <button
                type="button"
                class="btn btn-light rounded-pill"
                data-bs-dismiss="modal"
              >
                Cancelar
              </button>
              <button
                type="submit"
                class="btn btn-primary rounded-pill px-4"
                :disabled="!puedeAgregarLibre"
              >
                Agregar a la venta
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
    <!-- Modal precio para producto "Consultar precio" -->
    <div
      class="modal fade"
      id="modalPrecioPos"
      tabindex="-1"
      aria-hidden="true"
      ref="modalPrecioEl"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content border-0 shadow-lg rounded-4">
          <form @submit.prevent="confirmarPrecioProducto">
            <div class="modal-header border-0 pb-0">
              <div>
                <h2 class="modal-title fs-5 mb-1">Ingresar precio</h2>
                <p class="text-muted small mb-0">
                  Este producto está marcado como «Consultar precio» en el
                  catálogo.
                </p>
              </div>
              <button
                type="button"
                class="btn-close"
                data-bs-dismiss="modal"
                aria-label="Cerrar"
              ></button>
            </div>
            <div class="modal-body vstack gap-3">
              <div>
                <label class="form-label">Producto</label>
                <input
                  :value="formPrecio.nombre"
                  class="form-control"
                  type="text"
                  readonly
                />
              </div>
              <div class="row g-2">
                <div class="col-7">
                  <label class="form-label" for="precioPosInput"
                    >Monto ($)</label
                  >
                  <input
                    id="precioPosInput"
                    ref="precioPosInput"
                    v-model.number="formPrecio.precio"
                    type="number"
                    min="0"
                    step="0.01"
                    class="form-control form-control-lg"
                    placeholder="0"
                    required
                  />
                </div>
                <div class="col-5">
                  <label class="form-label" for="precioPosCant">Cantidad</label>
                  <input
                    id="precioPosCant"
                    v-model.number="formPrecio.cantidad"
                    type="number"
                    min="1"
                    step="1"
                    class="form-control form-control-lg"
                  />
                </div>
              </div>
            </div>
            <div class="modal-footer border-0 pt-0">
              <button
                type="button"
                class="btn btn-light rounded-pill"
                data-bs-dismiss="modal"
              >
                Cancelar
              </button>
              <button
                type="submit"
                class="btn btn-primary rounded-pill px-4"
                :disabled="!puedeConfirmarPrecioProducto"
              >
                Agregar a la venta
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Modal alta rápida de producto inexistente -->
    <div
      class="modal fade"
      id="modalNuevoProductoPos"
      tabindex="-1"
      aria-hidden="true"
      ref="modalNuevoProductoEl"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content border-0 shadow-lg rounded-4">
          <form
            ref="formNuevoProductoPos"
            @submit.prevent
            @keydown.enter="onEnterFormNuevoProducto"
          >
            <div class="modal-header border-0 pb-0">
              <div>
                <h2 class="modal-title fs-5 mb-1">Agregar producto</h2>
                <p class="text-muted small mb-0">
                  Completá los datos mínimos para cargarlo y venderlo.
                </p>
              </div>
              <button
                type="button"
                class="btn-close"
                data-bs-dismiss="modal"
                aria-label="Cerrar"
              ></button>
            </div>
            <div class="modal-body vstack gap-3">
              <div>
                <label class="form-label" for="nuevoProdNombre"
                  >Nombre <span class="text-danger">*</span></label
                >
                <input
                  id="nuevoProdNombre"
                  ref="nuevoProdNombreInput"
                  v-model.trim="formNuevoProducto.nombre"
                  class="form-control"
                  type="text"
                  placeholder="Ej: Agua mineral 500 ml"
                  maxlength="255"
                  autocomplete="off"
                  required
                />
              </div>
              <div>
                <label class="form-label" for="nuevoProdCategoria"
                  >Categoría <span class="text-danger">*</span></label
                >
                <div class="input-group">
                  <select
                    id="nuevoProdCategoria"
                    v-model="formNuevoProducto.categoria"
                    class="form-select"
                    required
                  >
                    <option disabled value="">Elegí una categoría</option>
                    <option
                      v-for="categoria in categorias"
                      :key="categoria.id || categoria.categoria_nombre"
                      :value="categoria.categoria_nombre"
                    >
                      {{ categoria.categoria_nombre }}
                    </option>
                  </select>
                  <button
                    type="button"
                    class="btn btn-outline-secondary"
                    title="Agregar categoría"
                    @click="mostrarAltaCategoriaPos = !mostrarAltaCategoriaPos"
                  >
                    <i class="bi bi-plus-lg" aria-hidden="true"></i>
                  </button>
                </div>
                <div
                  v-if="mostrarAltaCategoriaPos"
                  class="input-group mt-2"
                  data-pos-cat-alta
                >
                  <input
                    v-model="nuevaCategoriaNombre"
                    class="form-control"
                    type="text"
                    maxlength="100"
                    autocomplete="off"
                    placeholder="Nombre de la categoría"
                  />
                  <button
                    type="button"
                    class="btn btn-outline-primary"
                    :disabled="!puedeAgregarCategoriaPos || guardandoCategoriaPos"
                    @click="agregarCategoriaPos"
                  >
                    Crear
                  </button>
                </div>
              </div>
              <div>
                <label class="form-label" for="nuevoProdPrecio"
                  >Precio de venta <span class="text-danger">*</span></label
                >
                <div class="input-group">
                  <span class="input-group-text">$</span>
                  <input
                    id="nuevoProdPrecio"
                    v-model="formNuevoProducto.precio"
                    class="form-control"
                    type="number"
                    step="0.01"
                    placeholder="0"
                    required
                  />
                </div>
              </div>
              <div>
                <label class="form-label" for="nuevoProdCodigo"
                  >Código de barras</label
                >
                <div class="input-group">
                  <span class="input-group-text"
                    ><i class="bi bi-upc-scan" aria-hidden="true"></i
                  ></span>
                  <input
                    id="nuevoProdCodigo"
                    v-model.trim="formNuevoProducto.codigo_barras"
                    class="form-control font-monospace"
                    type="text"
                    placeholder="Escaneá o escribí el código"
                    autocomplete="off"
                    inputmode="numeric"
                  />
                </div>
              </div>
            </div>
            <div class="modal-footer border-0 pt-0 d-flex flex-wrap gap-2">
              <button
                type="button"
                class="btn btn-link text-decoration-none me-auto px-0"
                @click="irANuevoProductoCompleto"
              >
                Ver más
                <i class="bi bi-box-arrow-up-right ms-1" aria-hidden="true"></i>
              </button>
              <button
                type="button"
                class="btn btn-light rounded-pill"
                data-bs-dismiss="modal"
                :disabled="guardandoProductoNuevo"
              >
                Cancelar
              </button>
              <button
                type="button"
                class="btn btn-primary rounded-pill px-4"
                :disabled="!puedeGuardarProductoNuevo || guardandoProductoNuevo"
                @click="guardarProductoNuevoPos"
              >
                <span
                  v-if="guardandoProductoNuevo"
                  class="spinner-border spinner-border-sm me-1"
                  aria-hidden="true"
                ></span>
                {{ guardandoProductoNuevo ? "Guardando…" : "Guardar" }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import * as bootstrap from 'bootstrap';
import NavbarMain from '@/components/layout/NavbarMain.vue';
import { isOnline, onConnectivityChange, refreshConnectivity } from '@/utils/connectivity';
import {
  panelFullscreen,
  ensurePanelFullscreenReady,
  togglePanelFullscreen,
  exitPanelFullscreen,
} from '@/utils/panelFullscreen';
import { getUsuario, getVendedorNombre, getNombre, getLogin } from '@/utils/session';
import { hasFeature } from '@/utils/features';
import { mensajeBannerCaja } from '@/utils/cajaSesion';
import {
  deduplicarProductos,
  mergeProductosCatalogo,
  sortProductosEstables,
  sameUsuarioOffline,
  aplicarStockLocalCatalogo,
  resolveEstadoCaja,
  persistCajaEstado,
  warmPosOfflineCache,
  guardarProductoOffline,
} from '@/utils/posOffline/cache';
import {
  applyLocalAbrir,
  enqueueCajaOp,
} from '@/utils/posOffline/cajaSync';
import { printSaleTicket, printFiscalSaleTicket, printingHint } from '@/utils/ticketPrint';
import {
  guardarPosDraft,
  leerPosDraft,
  limpiarPosDraft,
  normalizarCantidadesPos,
} from "@/utils/posDraft";
import {
  APP_VERSION,
  SCHEMA_VERSION,
  discardOutboxEntry,
  enqueueOfflineSale,
  syncOutbox,
  countPendingOutbox,
  checkServerTimeOffset,
  exportOutboxBackup,
  listOutboxConflicts,
  resolveCajaMismatch,
  updateOutboxEntry,
  subscribePosSyncEvents,
} from "@/utils/posOffline/sync";
import {
  enrollDeviceIfNeeded,
  renewCertificateIfNeeded,
  reEnrollPosDevice,
} from "@/utils/posOffline/deviceEnrollment";
import {
  isDeviceBlocked,
  setDeviceBlocked,
  getDeviceRecord,
  generateUuid,
  getOrCreateDeviceUuid,
} from "@/utils/posOffline/device";
import {
  cachePosCatalog,
  cachePosSnapshot,
  getCachedPosCatalog,
  getCachedPosSnapshot,
  idbGetAll,
  cacheCategorias,
  obtenerCategoriasCatalogo,
  agregarCategoriaOffline,
} from "@/utils/posOffline/db";
import {
  buscarCatalogoPorCodigo,
  adoptarDesdeCatalogo,
} from "@/utils/catalogoAdopt";
import { catalogoOrdenMixin } from "@/utils/productCatalogOrder";
import { PRODUCT_PIN_VISTAS } from "@/utils/productPins";

export default {
  name: "PosView",
  components: { NavbarMain },
  mixins: [catalogoOrdenMixin(PRODUCT_PIN_VISTAS.pos)],
  data() {
    return {
      productos: [],
      clientes: [],
      cantidades: {},
      preciosPos: {},
      itemsLibres: [],
      libreSeq: 1,
      formLibre: { nombre: "", precio: null, cantidad: 1 },
      formPrecio: { productoId: null, nombre: "", precio: null, cantidad: 1 },
      modalLibre: null,
      modalPrecio: null,
      modalNuevoProducto: null,
      modalLibreAbierto: false,
      modalPrecioAbierto: false,
      modalNuevoProductoAbierto: false,
      scan: "",
      codigoInexistente: "",
      categorias: [],
      mostrarAltaCategoriaPos: false,
      nuevaCategoriaNombre: "",
      guardandoCategoriaPos: false,
      guardandoProductoNuevo: false,
      formNuevoProducto: {
        nombre: "",
        categoria: "",
        precio: null,
        codigo_barras: "",
      },
      clienteBusqueda: "",
      clienteDdAbierto: false,
      clienteId: "",
      clienteNombre: "",
      medioPago: "Efectivo",
      recibido: null,
      imprimir: true,
      facturarArca: false,
      arcaListo: false,
      arcaEmisor: null,
      arcaCargando: false,
      cobrando: false,
      adoptandoCatalogo: false,
      modoCobro: false,
      ticketCfg: {},
      negocio: {},
      medios: [
        { id: "Efectivo", label: "Efectivo", icon: "bi bi-cash" },
        { id: "Débito", label: "Débito", icon: "bi bi-credit-card-2-front" },
        { id: "Crédito", label: "Crédito", icon: "bi bi-credit-card" },
        { id: "Transferencia", label: "Transferencia", icon: "bi bi-bank" },
        { id: "QR / Mercado Pago", label: "QR / MP", icon: "bi bi-qr-code" },
        { id: "Point (Tarjeta)", label: "Point", icon: "bi bi-credit-card-2-back" },
      ],
      billetes: [1000, 2000, 5000, 10000, 20000],
      cajaAbierta: false,
      cajaRequerida: false,
      cajaId: null,
      abriendoCaja: false,
      formAbrirCaja: { monto_inicial: 0, observacion: "" },
      modalAbrirCaja: null,
      modalAbrirCajaAbierto: false,
      esMobile: false,
      panelMobile: "catalog",
      mobileMq: null,
      mpVinculado: false,
      mpPointVinculado: false,
      mpQr: {
        cargandoEstado: false,
        generando: false,
        pollando: false,
        pagado: false,
        orderId: null,
        image: null,
        error: "",
        timer: null,
      },
      mpPoint: {
        generando: false,
        pollando: false,
        pagado: false,
        fallido: false,
        orderId: null,
        medioPagoResuelto: null,
        error: "",
        timer: null,
      },
      offline: {
        pending: 0,
        blocked: false,
        timeWarning: false,
        deviceCertWarning: false,
        upgradeRequired: false,
        online: isOnline(),
        serverOffsetMs: 0,
        conflicts: [],
        syncing: false,
      },
      deviceBlocked: false,
      reenrolling: false,
      offConnectivity: null,
      offSyncEvents: null,
    };
  },
  computed: {
    mediosVisibles() {
      let lista = this.medios;
      if (!this.offline.online) {
        lista = lista.filter(
          (m) => m.id !== "QR / Mercado Pago" && m.id !== "Point (Tarjeta)",
        );
      } else if (this.mpPointVinculado) {
        lista = lista.filter((m) => m.id !== "QR / Mercado Pago");
      } else {
        lista = lista.filter((m) => m.id !== "Point (Tarjeta)");
      }
      return lista;
    },
    esMedioQr() {
      return this.medioPago === "QR / Mercado Pago";
    },
    esMedioPoint() {
      return this.medioPago === "Point (Tarjeta)";
    },
    pantallaCompleta() {
      return panelFullscreen.active;
    },
    mostrarCatalogo() {
      return !this.esMobile || this.panelMobile === "catalog";
    },
    mostrarVenta() {
      return !this.esMobile || this.panelMobile === "venta";
    },
    mostrarBarraMobile() {
      return (
        this.esMobile &&
        this.panelMobile === "catalog" &&
        this.lineas.length > 0 &&
        !this.modoCobro
      );
    },
    puedeConfirmarCobro() {
      if (this.cobrando || !this.lineas.length) return false;
      if (this.deviceBlocked || this.offline.upgradeRequired) return false;
      if (this.medioPago === "Efectivo" && this.vuelto < 0) return false;
      if (this.esMedioQr) {
        if (!this.offline.online) return false;
        if (!this.mpVinculado) return false;
        return !!this.mpQr.pagado;
      }
      if (this.esMedioPoint) {
        if (!this.offline.online) return false;
        if (!this.mpPointVinculado) return false;
        return !!this.mpPoint.pagado;
      }
      return true;
    },
    puedeFacturarArca() {
      return hasFeature("integracion_arca") && this.arcaListo;
    },
    cajaBanner() {
      return mensajeBannerCaja(
        {
          requerida: this.cajaRequerida,
          abierta: this.cajaAbierta,
          cajaId: this.cajaId,
        },
        { compact: true },
      );
    },
    cajaEtiqueta() {
      if (!this.cajaRequerida || !this.cajaAbierta) return "";
      return this.cajaId != null ? `Caja #${this.cajaId}` : "Caja abierta";
    },
    usuario() {
      return getUsuario();
    },
    clientesFiltrados() {
      const q = this.clienteBusqueda.trim().toLowerCase();
      if (!q) return this.clientes.slice(0, 50);
      return this.clientes.filter((c) => {
        const n = (c.nombre && String(c.nombre).toLowerCase()) || "";
        const t = (c.telefono && String(c.telefono).toLowerCase()) || "";
        return n.includes(q) || t.includes(q);
      });
    },
    nombreClienteSeleccionado() {
      if (!this.clienteId) return "";
      const c = this.clientes.find(
        (x) => String(x.cliente_id) === String(this.clienteId),
      );
      return c ? c.nombre : "";
    },
    productosFiltrados() {
      const q = this.scan.trim().toLowerCase();
      let list = deduplicarProductos(this.productos).filter((p) =>
        this.productoVisibleEnPos(p),
      );
      if (q) {
        list = list.filter((p) => {
          const nombre = (p.producto_nombre && p.producto_nombre.toLowerCase()) || "";
          const codigo =
            (p.producto_codigo_barras &&
              String(p.producto_codigo_barras).toLowerCase()) ||
            "";
          const categoria = (
            (p.producto_categoria && String(p.producto_categoria).trim()) ||
            "Sin categoría"
          ).toLowerCase();
          return nombre.includes(q) || codigo.includes(q) || categoria.includes(q);
        });
      }
      return this.ordenarCatalogo(deduplicarProductos(list));
    },
    puedeAgregarLibre() {
      const precio = Number(this.formLibre.precio);
      const cant = Math.floor(Number(this.formLibre.cantidad) || 0);
      return Number.isFinite(precio) && precio > 0 && cant >= 1;
    },
    puedeConfirmarPrecioProducto() {
      const precio = Number(this.formPrecio.precio);
      const cant = Math.floor(Number(this.formPrecio.cantidad) || 0);
      return (
        this.formPrecio.productoId != null &&
        Number.isFinite(precio) &&
        precio > 0 &&
        cant >= 1
      );
    },
    puedeGuardarProductoNuevo() {
      const nombre = String(this.formNuevoProducto.nombre || "").trim();
      const categoria = String(this.formNuevoProducto.categoria || "").trim();
      const precio = Number(this.formNuevoProducto.precio);
      return (
        !!nombre &&
        !!categoria &&
        this.formNuevoProducto.precio !== "" &&
        this.formNuevoProducto.precio != null &&
        Number.isFinite(precio)
      );
    },
    puedeAgregarCategoriaPos() {
      return Boolean(String(this.nuevaCategoriaNombre || "").trim());
    },
    lineas() {
      const out = [];
      for (const p of this.productos) {
        const q = this.cantidades[p.producto_id] || 0;
        if (q <= 0) continue;
        const unit = this.precioVentaProducto(p);
        if (unit == null) continue;
        out.push({
          key: `prod-${p.producto_id}`,
          libre: false,
          p,
          q,
          precioUnitario: unit,
          subtotal: q * unit,
        });
      }
      for (const it of this.itemsLibres) {
        const q = Math.max(1, Math.floor(Number(it.q) || 1));
        const precio = Number(it.precio) || 0;
        out.push({
          key: `libre-${it.id}`,
          libre: true,
          libreId: it.id,
          p: {
            producto_id: null,
            producto_nombre: it.nombre,
            producto_precio: precio,
            producto_categoria: "Cobro libre",
            producto_stock: -1,
          },
          q,
          precioUnitario: precio,
          subtotal: q * precio,
        });
      }
      return out;
    },
    total() {
      return this.lineas.reduce((a, r) => a + r.subtotal, 0);
    },
    vuelto() {
      return Number(this.recibido || 0) - this.total;
    },
    tieneDatosTransferencia() {
      return !!(
        this.negocio &&
        (String(this.negocio.pagoAlias || "").trim() ||
          String(this.negocio.pagoCvu || "").trim() ||
          String(this.negocio.pagoTitular || "").trim() ||
          String(this.negocio.pagoBanco || "").trim())
      );
    },
  },
  watch: {
    cantidades: {
      deep: true,
      handler() {
        this.persistirPosDraft();
      },
    },
    itemsLibres: {
      deep: true,
      handler() {
        this.persistirPosDraft();
      },
    },
    preciosPos: {
      deep: true,
      handler() {
        this.persistirPosDraft();
      },
    },
    scan(val) {
      this.persistirPosDraft();
      const code = String(val || "").trim();
      if (this.codigoInexistente && code !== this.codigoInexistente) {
        this.codigoInexistente = "";
      }
    },
    lineas(val) {
      if (this.modoCobro && !val.length) {
        this.modoCobro = false;
      }
    },
    medioPago(val) {
      if (val === "QR / Mercado Pago") {
        if (!isOnline()) {
          this.medioPago = "Efectivo";
          Swal.fire({
            icon: "info",
            title: "Mercado Pago requiere internet",
            text: "En modo offline podés cobrar en efectivo, tarjeta o transferencia.",
          });
          return;
        }
        if (!this.mpVinculado) {
          this.medioPago = "Efectivo";
          return;
        }
        if (this.mpVinculado && !this.mpQr.image && !this.mpQr.generando) {
          this.generarQrMp();
        }
      } else {
        this.resetQrMp();
      }
      if (val !== "Point (Tarjeta)") {
        this.detenerPollPoint();
      }
    },
    modoCobro(val) {
      if (!val) {
        this.resetQrMp();
        this.resetPoint();
      }
    },
  },
  beforeUnmount() {
    this.detenerPollQr();
    this.detenerPollPoint();

    this.persistirPosDraft();
    document.removeEventListener(
      "visibilitychange",
      this.onPosVisibilityChange,
    );
    document.removeEventListener("click", this.onDocClick);
    document.removeEventListener("keydown", this.onPosKeydown);
    if (this.offConnectivity) this.offConnectivity();
    if (this.offSyncEvents) this.offSyncEvents();
    if (this.mobileMq) {
      if (this.mobileMq.removeEventListener) {
        this.mobileMq.removeEventListener("change", this.onMobileMediaChange);
      } else if (this.mobileMq.removeListener) {
        this.mobileMq.removeListener(this.onMobileMediaChange);
      }
    }
    if (this.$refs.modalAbrirCajaEl) {
      this.$refs.modalAbrirCajaEl.removeEventListener(
        "shown.bs.modal",
        this.onModalAbrirCajaShown,
      );
      this.$refs.modalAbrirCajaEl.removeEventListener(
        "hidden.bs.modal",
        this.onModalAbrirCajaHidden,
      );
    }
    if (this.$refs.modalLibreEl) {
      this.$refs.modalLibreEl.removeEventListener(
        "shown.bs.modal",
        this.onModalLibreShown,
      );
      this.$refs.modalLibreEl.removeEventListener(
        "hidden.bs.modal",
        this.onModalLibreHidden,
      );
    }
    if (this.$refs.modalPrecioEl) {
      this.$refs.modalPrecioEl.removeEventListener(
        "shown.bs.modal",
        this.onModalPrecioShown,
      );
      this.$refs.modalPrecioEl.removeEventListener(
        "hidden.bs.modal",
        this.onModalPrecioHidden,
      );
    }
    if (this.$refs.modalNuevoProductoEl) {
      this.$refs.modalNuevoProductoEl.removeEventListener(
        "shown.bs.modal",
        this.onModalNuevoProductoShown,
      );
      this.$refs.modalNuevoProductoEl.removeEventListener(
        "hidden.bs.modal",
        this.onModalNuevoProductoHidden,
      );
    }
    if (this.modalAbrirCaja) this.modalAbrirCaja.dispose();
    if (this.modalLibre) this.modalLibre.dispose();
    if (this.modalPrecio) this.modalPrecio.dispose();
    if (this.modalNuevoProducto) this.modalNuevoProducto.dispose();
    if (
      this.modalAbrirCajaAbierto ||
      this.modalLibreAbierto ||
      this.modalPrecioAbierto ||
      this.modalNuevoProductoAbierto
    ) {
      document.querySelectorAll(".modal-backdrop").forEach((n) => n.remove());
      document.body.classList.remove("modal-open");
      document.body.style.removeProperty("overflow");
      document.body.style.removeProperty("padding-right");
      document.body.style.removeProperty("padding-left");
    }
  },
  async mounted() {
    if (!this.usuario || this.usuario === "admin") {
      this.$router.push("/u/home");
      return;
    }
    this.modalAbrirCaja = new bootstrap.Modal(this.$refs.modalAbrirCajaEl);
    this.$refs.modalAbrirCajaEl.addEventListener(
      "shown.bs.modal",
      this.onModalAbrirCajaShown,
    );
    this.$refs.modalAbrirCajaEl.addEventListener(
      "hidden.bs.modal",
      this.onModalAbrirCajaHidden,
    );
    this.modalLibre = new bootstrap.Modal(this.$refs.modalLibreEl);
    this.$refs.modalLibreEl.addEventListener(
      "shown.bs.modal",
      this.onModalLibreShown,
    );
    this.$refs.modalLibreEl.addEventListener(
      "hidden.bs.modal",
      this.onModalLibreHidden,
    );
    this.modalPrecio = new bootstrap.Modal(this.$refs.modalPrecioEl);
    this.$refs.modalPrecioEl.addEventListener(
      "shown.bs.modal",
      this.onModalPrecioShown,
    );
    this.$refs.modalPrecioEl.addEventListener(
      "hidden.bs.modal",
      this.onModalPrecioHidden,
    );
    this.modalNuevoProducto = new bootstrap.Modal(
      this.$refs.modalNuevoProductoEl,
    );
    this.$refs.modalNuevoProductoEl.addEventListener(
      "shown.bs.modal",
      this.onModalNuevoProductoShown,
    );
    this.$refs.modalNuevoProductoEl.addEventListener(
      "hidden.bs.modal",
      this.onModalNuevoProductoHidden,
    );
    this.mobileMq = window.matchMedia("(max-width: 991.98px)");
    this.syncMobile(this.mobileMq.matches);
    if (this.mobileMq.addEventListener) {
      this.mobileMq.addEventListener("change", this.onMobileMediaChange);
    } else if (this.mobileMq.addListener) {
      this.mobileMq.addListener(this.onMobileMediaChange);
    }
    document.addEventListener("keydown", this.onPosKeydown);
    ensurePanelFullscreenReady();
    document.addEventListener("visibilitychange", this.onPosVisibilityChange);
    let wasOnline = null;
    this.offConnectivity = onConnectivityChange((online) => {
      this.offline.online = online;
      if (wasOnline === null) {
        wasOnline = online;
        return;
      }
      if (online && !wasOnline) {
        void this.onConnectivityRestored();
      } else if (!online && wasOnline) {
        this.onConnectivityLost();
      }
      wasOnline = online;
    });
    this.offSyncEvents = subscribePosSyncEvents(() => {
      void this.refreshOutboxState();
    });
    await this.cargar();
    if (isOnline()) {
      await this.cargarEstadoMp();
      if (hasFeature("integracion_arca")) await this.cargarEstadoArca();
    }
    await this.initOfflinePos();
    if (this.offline.online) {
      void warmPosOfflineCache(this.usuario);
    }
    if (this.cajaRequerida && !this.cajaAbierta && this.offline.online) {
      this.abrirModalCaja(false);
    }
    document.addEventListener("click", this.onDocClick);
    this.enfocarScan();
  },
  methods: {
    async initOfflinePos() {
      try {
        if (await isDeviceBlocked()) {
          this.deviceBlocked = true;
          await this.refreshOutboxState();
          return;
        }
        const time = await checkServerTimeOffset();
        this.offline.timeWarning = time.warning;
        this.offline.serverOffsetMs = time.offsetMs || 0;
        this.offline.upgradeRequired = !!time.upgradeRequired;
        if (isOnline()) {
          await enrollDeviceIfNeeded({ negocio: this.usuario });
        }
        let device = await getDeviceRecord(this.usuario);
        if (device && device.cert_vence_at) {
          const days =
            (new Date(device.cert_vence_at).getTime() - Date.now()) / 86400000;
          this.offline.deviceCertWarning = days <= 30;
        }
        if (isOnline()) {
          await renewCertificateIfNeeded({ negocio: this.usuario });
          device = await getDeviceRecord(this.usuario);
          if (device && device.cert_vence_at) {
            const days =
              (new Date(device.cert_vence_at).getTime() - Date.now()) /
              86400000;
            this.offline.deviceCertWarning = days <= 30;
          }
        }
        this.offline.pending = await countPendingOutbox();
        this.offline.conflicts = await listOutboxConflicts();
        if (isOnline()) {
          await this.syncPendingSales();
        }
      } catch (e) {
        console.warn("[pos offline]", e);
      }
    },
    async refreshOutboxState() {
      this.offline.pending = await countPendingOutbox();
      this.offline.conflicts = await listOutboxConflicts();
    },
    async onConnectivityRestored() {
      this.offline.online = true;
      try {
        const time = await checkServerTimeOffset();
        this.offline.timeWarning = time.warning;
        this.offline.serverOffsetMs = time.offsetMs || 0;
        this.offline.upgradeRequired = !!time.upgradeRequired;
        await enrollDeviceIfNeeded({ negocio: this.usuario });
        await renewCertificateIfNeeded({ negocio: this.usuario });
        const device = await getDeviceRecord(this.usuario);
        if (device && device.cert_vence_at) {
          const days =
            (new Date(device.cert_vence_at).getTime() - Date.now()) / 86400000;
          this.offline.deviceCertWarning = days <= 30;
        }
        await this.cargar();
        void warmPosOfflineCache(this.usuario);
        await this.cargarEstadoMp();
      } catch (_) {
        /* sync will expose actionable errors */
      }
      await this.syncPendingSales();
    },
    onConnectivityLost() {
      this.offline.online = false;
      this.detenerPollQr();
      if (this.esMedioQr) this.medioPago = "Efectivo";
    },
    async syncPendingSales() {
      if (this.offline.syncing) return;
      this.offline.syncing = true;
      try {
        const result = await syncOutbox();
        this.offline.pending = await countPendingOutbox();
        this.offline.conflicts = await listOutboxConflicts();
        if (result && result.synced > 0) {
          await this.cargar();
        }
      } catch (e) {
        const code =
          e.code || (e.response && e.response.data && e.response.data.code);
        if (code === "DEVICE_REVOKED" || code === "DEVICE_REPLACED") {
          await setDeviceBlocked(true, code);
          this.deviceBlocked = true;
          this.offline.pending = await countPendingOutbox();
        } else if (code === "DEVICE_NOT_FOUND") {
          Swal.fire({
            icon: "warning",
            title: "Dispositivo no registrado",
            text: "No se pudo registrar este equipo en el servidor. Probá re-enrolar desde el aviso de dispositivo bloqueado o recargá el POS estando online.",
          });
        }
      } finally {
        this.offline.syncing = false;
      }
    },
    async exportOfflineBackup() {
      const blob = new Blob([await exportOutboxBackup()], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `puestify-outbox-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
    },
    async onReEnrollDeviceClick() {
      if (!isOnline()) {
        return Swal.fire({
          icon: "warning",
          text: "Necesitás conexión para re-enrolar este dispositivo.",
        });
      }
      if (this.reenrolling) return;
      this.reenrolling = true;
      try {
        await reEnrollPosDevice({ negocio: this.usuario });
        this.deviceBlocked = false;
        await this.syncPendingSales();
        return Swal.fire({
          icon: "success",
          title: "Dispositivo re-enrolado",
          text: "Este equipo ya puede sincronizar ventas offline. La impresión usa Printify o Chrome kiosk, según la config del ticket.",
        });
      } catch (e) {
        const message =
          (e.response && e.response.data && e.response.data.message) ||
          "El dispositivo continúa revocado. Contactá al administrador para reemplazarlo.";
        return Swal.fire({
          icon: "error",
          title: "No se pudo re-enrolar",
          text: message,
        });
      } finally {
        this.reenrolling = false;
      }
    },
    estadoConflicto(item) {
      if (item.sync_status === "caja_mismatch") return "la caja activa cambió";
      if (item.sync_status === "upgrade_required")
        return "requiere actualizar el POS";
      if (item.sync_status === "synced_with_warnings")
        return "sincronizada con avisos de stock";
      return item.sync_status;
    },
    async adjuntarConflictoCaja(item) {
      await this.cargarCaja();
      if (!this.cajaId) {
        return Swal.fire({
          icon: "warning",
          text: "Abrí una caja antes de resolver esta venta.",
        });
      }
      const confirm = await Swal.fire({
        icon: "question",
        title: "¿Adjuntar a la caja actual?",
        text: `La venta ${item.ticket_folio || ""} se registrará en la caja #${
          this.cajaId
        }.`,
        showCancelButton: true,
        confirmButtonText: "Sí, adjuntar",
      });
      if (!confirm.isConfirmed) return;
      await resolveCajaMismatch(item.client_mutation_id, this.cajaId);
      await this.syncPendingSales();
    },
    async descartarConflicto(item) {
      const confirm = await Swal.fire({
        icon: "warning",
        title: "Descartar venta pendiente",
        text: "La venta quedará conservada como descartada en el respaldo local.",
        showCancelButton: true,
        confirmButtonText: "Descartar",
        confirmButtonColor: "#dc3545",
      });
      if (!confirm.isConfirmed) return;
      await discardOutboxEntry(
        item.client_mutation_id,
        "Descartada desde resolución de caja",
      );
      this.offline.pending = await countPendingOutbox();
      this.offline.conflicts = await listOutboxConflicts();
    },
    async confirmarAvisoStock(item) {
      await updateOutboxEntry(item.client_mutation_id, {
        sync_status: "synced",
        warnings_acknowledged_at: new Date().toISOString(),
      });
      this.offline.conflicts = await listOutboxConflicts();
    },
    async togglePantallaCompleta() {
      if (this.esMobile) return;
      await togglePanelFullscreen();
      this.$nextTick(() => this.enfocarScan());
    },
    async salirPantallaCompleta() {
      await exitPanelFullscreen();
    },
    onPosVisibilityChange() {
      if (document.visibilityState === "hidden") {
        this.persistirPosDraft();
        return;
      }
      refreshConnectivity();
      void this.onPosTabVisible();
    },
    async onPosTabVisible() {
      await this.refreshOutboxState();
      await this.restaurarPosDraft();
      if (isOnline()) {
        await this.syncPendingSales();
      }
    },
    persistirPosDraft() {
      if (!this.usuario) return;
      const tieneCarrito =
        Object.keys(this.cantidades).length > 0 || this.itemsLibres.length > 0;
      const tieneBusqueda = !!this.scan.trim();
      if (!tieneCarrito && !tieneBusqueda) {
        limpiarPosDraft(this.usuario);
        void cachePosSnapshot(`draft:${this.usuario}`, null);
        return;
      }
      const draft = {
        cantidades: this.cantidades,
        preciosPos: this.preciosPos,
        itemsLibres: this.itemsLibres,
        scan: this.scan,
      };
      guardarPosDraft(this.usuario, draft);
      void cachePosSnapshot(`draft:${this.usuario}`, draft);
    },
    async restaurarPosDraft() {
      const draft =
        (await getCachedPosSnapshot(`draft:${this.usuario}`)) ||
        leerPosDraft(this.usuario);
      if (!draft) return;
      const cantidades = normalizarCantidadesPos(
        draft.cantidades,
        this.productos,
      );
      if (Object.keys(cantidades).length) {
        this.cantidades = cantidades;
      }
      const precios = {};
      for (const [id, raw] of Object.entries(draft.preciosPos || {})) {
        const n = Number(raw);
        if (!Number.isFinite(n) || n <= 0) continue;
        const qty = cantidades[id] || cantidades[Number(id)];
        if (!qty) continue;
        const key = cantidades[id] != null ? id : Number(id);
        precios[key] = n;
      }
      this.preciosPos = precios;
      if (Array.isArray(draft.itemsLibres) && draft.itemsLibres.length) {
        const libres = [];
        let maxId = 0;
        for (const raw of draft.itemsLibres) {
          const precio = Number(raw && raw.precio);
          const q = Math.max(1, Math.floor(Number(raw && raw.q) || 1));
          const nombre =
            String((raw && raw.nombre) || "").trim() || "Cobro libre";
          if (!Number.isFinite(precio) || precio <= 0) continue;
          const id = Number(raw.id) || ++maxId;
          if (id > maxId) maxId = id;
          libres.push({ id, nombre: nombre.slice(0, 120), precio, q });
        }
        this.itemsLibres = libres;
        this.libreSeq = Math.max(this.libreSeq, maxId + 1);
      }
      if (draft.scan) {
        this.scan = String(draft.scan);
      }
    },
    onModalAbrirCajaShown() {
      this.modalAbrirCajaAbierto = true;
    },
    onModalAbrirCajaHidden() {
      this.modalAbrirCajaAbierto = false;
      this.enfocarScan();
    },
    cerrarCobro() {
      this.modoCobro = false;
      this.resetQrMp();
      this.resetPoint();
      if (!this.esMobile) {
        this.enfocarScan();
      }
    },
    /** Si estaba cobrando y se carga otro ítem, vuelve a la venta / catálogo. */
    salirDeCobroPorNuevaCarga() {
      if (!this.modoCobro) return;
      this.cerrarCobro();
      if (this.esMobile) {
        this.panelMobile = "catalog";
      }
    },
    async cargarEstadoMp() {
      this.mpQr.cargandoEstado = true;
      try {
        const { data } = await axios.get("/mp/estado");
        this.mpVinculado = !!data.vinculado;
        this.mpPointVinculado = !!data.point_terminal_id;
      } catch {
        this.mpVinculado = false;
        this.mpPointVinculado = false;
      } finally {
        this.mpQr.cargandoEstado = false;
      }
      if (this.esMedioQr && !this.mpVinculado) {
        this.medioPago = "Efectivo";
      }
    },
    medioPagoDeshabilitado(m) {
      if (!m) return true;
      if (m.id === "QR / Mercado Pago") {
        return !this.mpVinculado || this.mpQr.cargandoEstado;
      }
      return false;
    },
    tituloMedioPago(m) {
      if (m && m.id === "QR / Mercado Pago" && !this.mpVinculado) {
        return "Vinculá Mercado Pago en Perfil para cobrar con QR";
      }
      return m ? m.label : "";
    },
    seleccionarMedioPago(m) {
      if (!m || this.medioPagoDeshabilitado(m)) return;
      this.medioPago = m.id;
    },
    detenerPollQr() {
      if (this.mpQr.timer) {
        clearInterval(this.mpQr.timer);
        this.mpQr.timer = null;
      }
      this.mpQr.pollando = false;
    },
    resetQrMp() {
      this.detenerPollQr();
      this.mpQr.generando = false;
      this.mpQr.pagado = false;
      this.mpQr.orderId = null;
      this.mpQr.image = null;
      this.mpQr.error = "";
    },
    marcarQrPagadoManual() {
      this.detenerPollQr();
      this.mpQr.pagado = true;
    },
    cancelarCobroQrManual() {
      this.resetQrMp();
      this.medioPago = "Efectivo";
    },
    async generarQrMp() {
      if (!this.mpVinculado || !this.total) return;
      this.detenerPollQr();
      this.mpQr.generando = true;
      this.mpQr.pagado = false;
      this.mpQr.error = "";
      this.mpQr.image = null;
      this.mpQr.orderId = null;
      try {
        const { data } = await axios.post("/mp/pos/qr", {
          total: this.total,
          description: `Venta POS ${this.usuario}`,
          items: this.lineas.map((r) => ({
            title: r.p.producto_nombre,
            unit_price: r.precioUnitario,
            quantity: r.q,
          })),
        });
        this.mpQr.orderId = data.order_id;
        this.mpQr.image = data.qr_image;
        this.iniciarPollQr();
      } catch (e) {
        const d = e.response && e.response.data;
        if (d && d.code === "MP_NO_VINCULADO") {
          this.mpVinculado = false;
        }
        this.mpQr.error = (d && d.message) || "No se pudo generar el QR.";
      } finally {
        this.mpQr.generando = false;
      }
    },
    iniciarPollQr() {
      this.detenerPollQr();
      if (!this.mpQr.orderId) return;
      this.mpQr.pollando = true;
      this.mpQr.timer = setInterval(() => {
        this.consultarEstadoQr();
      }, 3000);
      this.consultarEstadoQr();
    },
    async consultarEstadoQr() {
      if (!this.mpQr.orderId || this.mpQr.pagado) return;
      try {
        const { data } = await axios.get(
          `/mp/pos/qr/${encodeURIComponent(this.mpQr.orderId)}`,
        );
        if (data && data.paid) {
          this.mpQr.pagado = true;
          this.detenerPollQr();
          this.posToast({ ariaLabel: "Pago QR recibido" });
        }
      } catch {
        /* silencioso: se reintenta en el próximo poll */
      }
    },
    detenerPollPoint() {
      if (this.mpPoint.timer) {
        clearInterval(this.mpPoint.timer);
        this.mpPoint.timer = null;
      }
      this.mpPoint.pollando = false;
    },
    resetPoint() {
      this.detenerPollPoint();
      this.mpPoint.generando = false;
      this.mpPoint.pagado = false;
      this.mpPoint.fallido = false;
      this.mpPoint.orderId = null;
      this.mpPoint.medioPagoResuelto = null;
      this.mpPoint.error = "";
    },
    async cancelarCobroPointSilencioso() {
      const orderId = this.mpPoint.orderId;
      this.resetPoint();
      if (!orderId) return;
      try {
        await axios.post(`/mp/pos/point/${encodeURIComponent(orderId)}/cancel`);
      } catch (e) {
        this.avisarSiNoSePudoCancelarEnTerminal(e);
      }
    },
    async cancelarCobroPointManual() {
      await this.cancelarCobroPointSilencioso();
    },
    avisarSiNoSePudoCancelarEnTerminal(e) {
      const d = e.response && e.response.data;
      if (d && d.code === "MP_CANNOT_CANCEL") {
        Swal.mixin({
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 6000,
        }).fire({
          icon: "warning",
          title: "Cancelalo desde la terminal",
          text:
            d.message ||
            "El cobro ya llegó a la pantalla de la Point: cancelalo ahí o esperá a que expire.",
        });
      }
    },
    async generarCobroPoint() {
      if (!this.mpPointVinculado || !this.total) return;
      this.detenerPollPoint();
      this.mpPoint.generando = true;
      this.mpPoint.pagado = false;
      this.mpPoint.fallido = false;
      this.mpPoint.error = "";
      this.mpPoint.orderId = null;
      this.mpPoint.medioPagoResuelto = null;
      try {
        const { data } = await axios.post("/mp/pos/point", {
          total: this.total,
          description: `Venta POS ${this.usuario}`,
        });
        this.mpPoint.orderId = data.order_id;
        this.iniciarPollPoint();
      } catch (e) {
        const d = e.response && e.response.data;
        if (d && d.code === "MP_SIN_TERMINAL") this.mpPointVinculado = false;
        this.mpPoint.error =
          (d && d.message) || "No se pudo enviar el cobro a la terminal.";
      } finally {
        this.mpPoint.generando = false;
      }
    },
    iniciarPollPoint() {
      this.detenerPollPoint();
      if (!this.mpPoint.orderId) return;
      this.mpPoint.pollando = true;
      this.mpPoint.timer = setInterval(() => this.consultarEstadoPoint(), 3000);
      this.consultarEstadoPoint();
    },
    async consultarEstadoPoint() {
      if (!this.mpPoint.orderId || this.mpPoint.pagado) return;
      try {
        const { data } = await axios.get(
          `/mp/pos/point/${encodeURIComponent(this.mpPoint.orderId)}`,
        );
        if (data && data.paid) {
          this.mpPoint.pagado = true;
          if (data.medio_pago) this.mpPoint.medioPagoResuelto = data.medio_pago;
          this.detenerPollPoint();
          this.posToast({ ariaLabel: "Pago recibido en terminal" });
          return;
        }
        const status = data && data.status;
        if (["failed", "canceled", "expired"].includes(status)) {
          this.mpPoint.fallido = true;
          this.mpPoint.error =
            "El cobro no se completó en la terminal. Probá de nuevo.";
          this.detenerPollPoint();
        }
      } catch {
        /* silencioso */
      }
    },
    medioPagoEfectivoCobro() {
      if (this.esMedioPoint) {
        return this.mpPoint.medioPagoResuelto || "Tarjeta";
      }
      return this.medioPago;
    },
    onMobileMediaChange(e) {
      this.syncMobile(e.matches);
    },
    syncMobile(isMobile) {
      this.esMobile = isMobile;
      if (!isMobile) {
        this.panelMobile = "catalog";
      } else if (this.pantallaCompleta) {
        this.salirPantallaCompleta();
      }
    },
    irAVentaMobile() {
      this.panelMobile = "venta";
    },
    irACatalogoMobile() {
      if (this.modoCobro) {
        this.modoCobro = false;
      }
      this.panelMobile = "catalog";
      this.$nextTick(() => this.enfocarScan());
    },
    onPosKeydown(e) {
      if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;
      if (
        this.modalAbrirCajaAbierto ||
        this.modalLibreAbierto ||
        this.modalPrecioAbierto ||
        this.modalNuevoProductoAbierto
      )
        return;

      if (e.key === "F2") {
        e.preventDefault();
        if (this.modoCobro) {
          if (this.puedeConfirmarCobro) this.cobrar();
          return;
        }
        if (this.lineas.length) this.abrirCobro();
        return;
      }

      if (e.key === "F3") {
        e.preventDefault();
        this.enfocarScan();
        return;
      }

      if (e.key === "F4") {
        e.preventDefault();
        if (this.modoCobro) return;
        if (this.lineas.length) this.confirmarVaciar();
        return;
      }

      if (e.key === "F6") {
        e.preventDefault();
        this.abrirModalLibre();
        return;
      }

      if (e.key === "Escape") {
        if (this.modoCobro) {
          e.preventDefault();
          this.cerrarCobro();
          return;
        }
        if (this.esMobile && this.panelMobile === "venta") {
          e.preventDefault();
          this.irACatalogoMobile();
          return;
        }
        if (this.clienteDdAbierto) {
          e.preventDefault();
          this.cerrarClientesDd();
          return;
        }
        if (this.scan.trim()) {
          e.preventDefault();
          this.limpiarBusqueda();
        }
        return;
      }

      if (this.modoCobro && /^[1-5]$/.test(e.key)) {
        if (this.esMedioQr) return;
        const recibidoEl = this.$refs.recibidoInput;
        // Con "Paga con" en focus, los dígitos van al monto (no cambian el medio).
        if (
          recibidoEl &&
          (e.target === recibidoEl || document.activeElement === recibidoEl)
        ) {
          return;
        }
        const medio = this.mediosVisibles[Number(e.key) - 1];
        if (medio && !this.medioPagoDeshabilitado(medio)) {
          e.preventDefault();
          this.seleccionarMedioPago(medio);
        }
        return;
      }

      if (this.modoCobro && e.key === "Enter" && this.puedeConfirmarCobro) {
        const t = e.target;
        if (t && t.tagName === "TEXTAREA") return;
        if (t && t.tagName === "INPUT") {
          const type = String(t.type || "text").toLowerCase();
          if (type === "checkbox" || type === "text" || type === "search")
            return;
        }
        e.preventDefault();
        this.cobrar();
        return;
      }

      if (this.debeCapturarTecladoBusqueda(e)) {
        this.redirigirTeclaABusqueda(e);
      }
    },
    debeCapturarTecladoBusqueda(e) {
      if (
        this.modalAbrirCajaAbierto ||
        this.modalLibreAbierto ||
        this.modalPrecioAbierto ||
        this.modalNuevoProductoAbierto
      )
        return false;

      const t = e.target;
      // Si ya hay un campo activo (modal, cobro, etc.), no robar las teclas.
      if (
        t &&
        (t.tagName === "INPUT" ||
          t.tagName === "TEXTAREA" ||
          t.tagName === "SELECT" ||
          t.isContentEditable)
      ) {
        return false;
      }
      if (t && typeof t.closest === "function" && t.closest(".modal.show")) {
        return false;
      }

      if (e.ctrlKey || e.metaKey || e.altKey) return false;
      if (e.key === "F2" || e.key === "F3" || e.key === "F4" || e.key === "F6")
        return false;

      if (e.key === "Enter") return !!this.scan.trim();
      if (e.key === "Backspace") return true;
      return e.key.length === 1;
    },
    redirigirTeclaABusqueda(e) {
      e.preventDefault();
      this.salirDeCobroPorNuevaCarga();
      this.enfocarScan();

      if (e.key === "Enter") {
        this.procesarScan();
        return;
      }

      if (e.key === "Backspace") {
        this.scan = this.scan.slice(0, -1);
        return;
      }

      if (e.key.length === 1) {
        this.scan += e.key;
      }
    },
    async abrirModalCaja(revalidar = true) {
      if (revalidar) {
        await this.cargarCaja();
        if (this.cajaAbierta) return;
      }
      this.formAbrirCaja = { monto_inicial: 0, observacion: "" };
      if (this.modalAbrirCaja) this.modalAbrirCaja.show();
    },
    async confirmarAbrirCajaRapida() {
      if (this.abriendoCaja) return;
      this.abriendoCaja = true;
      try {
        await this.cargarCaja();
        if (this.cajaAbierta) {
          if (this.modalAbrirCaja) this.modalAbrirCaja.hide();
          Swal.fire({
            toast: true,
            position: "top-end",
            icon: "info",
            title: `Ya hay una caja abierta${
              this.cajaId ? " #" + this.cajaId : ""
            }`,
            timer: 2200,
            showConfirmButton: false,
          });
          return;
        }
        if (!isOnline()) {
          const localId = `local_${generateUuid()}`;
          await enqueueCajaOp({
            tipo: "abrir",
            usuario: this.usuario,
            payload: {
              monto_inicial: this.formAbrirCaja.monto_inicial || 0,
              observacion:
                this.formAbrirCaja.observacion || "Apertura desde POS (offline)",
              local_caja_id: localId,
            },
          });
          const local = await applyLocalAbrir(this.usuario, {
            monto_inicial: this.formAbrirCaja.monto_inicial || 0,
            observacion:
              this.formAbrirCaja.observacion || "Apertura desde POS (offline)",
            local_caja_id: localId,
            actor_login: getLogin(),
            actor_nombre: getNombre() || getLogin(),
          });
          this.cajaRequerida = true;
          this.cajaAbierta = true;
          this.cajaId = local.cajaId;
          if (this.modalAbrirCaja) this.modalAbrirCaja.hide();
          Swal.fire({
            toast: true,
            position: "top-end",
            icon: "success",
            title: "Caja abierta (pendiente de sync)",
            timer: 2200,
            showConfirmButton: false,
          });
          this.enfocarScan();
          return;
        }
        await enrollDeviceIfNeeded({ negocio: this.usuario });
        const device_uuid = await getOrCreateDeviceUuid();
        const { data } = await axios.post("/cajas/abrir", {
          monto_inicial: this.formAbrirCaja.monto_inicial || 0,
          observacion_apertura:
            this.formAbrirCaja.observacion || "Apertura desde POS",
          client_mutation_id: generateUuid(),
          device_uuid,
        });
        if (this.modalAbrirCaja) this.modalAbrirCaja.hide();
        await persistCajaEstado(this.usuario, {
          requerida: true,
          abierta: true,
          cajaId: data.caja && data.caja.caja_id,
          caja: data.caja,
          resumen: data.resumen,
          resumen_server: data.resumen || null,
        });
        await this.cargarCaja();
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: "Caja abierta",
          timer: 2200,
          showConfirmButton: false,
        });
        this.enfocarScan();
      } catch (e) {
        const msg = e.response && e.response.data && e.response.data.message;
        Swal.fire({
          icon: "error",
          title: "No se pudo abrir la caja",
          text: msg || (e && e.message) || "",
        });
      } finally {
        this.abriendoCaja = false;
      }
    },
    onDocClick(e) {
      const wrap = this.$refs.clienteWrap;
      if (wrap && !wrap.contains(e.target)) {
        this.clienteDdAbierto = false;
      }
    },
    abrirClientesDd() {
      this.clienteDdAbierto = true;
    },
    cerrarClientesDd() {
      this.clienteDdAbierto = false;
    },
    seleccionarCliente(c) {
      this.clienteId = String(c.cliente_id);
      this.clienteNombre = c.nombre || "";
      this.clienteBusqueda = c.nombre || "";
      this.clienteDdAbierto = false;
    },
    limpiarCliente() {
      this.clienteId = "";
      this.clienteBusqueda = "";
      this.clienteNombre = "";
      this.clienteDdAbierto = false;
    },
    resetClienteCobro() {
      this.clienteId = "";
      this.clienteBusqueda = "";
      this.clienteNombre = "";
      this.clienteDdAbierto = false;
    },
    money(v) {
      return Number(v || 0).toLocaleString("es-AR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    esConsultarPrecio(p) {
      return Number(p && p.producto_precio) < 0;
    },
    productoVisibleEnPos(p) {
      if (p == null || p.producto_en_pos == null || p.producto_en_pos === "") {
        return true;
      }
      return Number(p.producto_en_pos) === 1;
    },
    precioVentaProducto(p) {
      if (!p) return null;
      const id = p.producto_id;
      const override =
        this.preciosPos[id] != null
          ? this.preciosPos[id]
          : this.preciosPos[String(id)];
      if (override != null) {
        const n = Number(override);
        return Number.isFinite(n) && n > 0 ? n : null;
      }
      if (this.esConsultarPrecio(p)) return null;
      const n = Number(p.producto_precio);
      return Number.isFinite(n) && n >= 0 ? n : null;
    },
    sinStock(p) {
      return (
        Number(p.producto_stock) !== -1 &&
        Number(p.producto_stock) <= (this.cantidades[p.producto_id] || 0)
      );
    },
    stockMaxProducto(p) {
      const s = Number(p && p.producto_stock);
      if (!Number.isFinite(s) || s === -1) return null;
      return Math.max(0, Math.floor(s));
    },
    stockMaxAttr(row) {
      if (!row || row.libre) return undefined;
      const max = this.stockMaxProducto(row.p);
      return max == null ? undefined : max;
    },
    puedeSumarLinea(row) {
      if (!row || row.libre) return true;
      const max = this.stockMaxProducto(row.p);
      return max == null || row.q < max;
    },
    enfocarScan() {
      // En mobile no auto-enfocar: abre el teclado y molesta al navegar el catálogo.
      if (this.esMobile) return;
      this.$nextTick(
        () => this.$refs.scanInput && this.$refs.scanInput.focus(),
      );
    },
    cant(p) {
      return this.cantidades[p.producto_id] || 0;
    },
    add(p, delta) {
      const max = this.stockMaxProducto(p);
      let next = Math.max(0, this.cant(p) + delta);
      if (max != null) next = Math.min(next, max);
      const copy = { ...this.cantidades };
      if (next === 0) {
        delete copy[p.producto_id];
        const precios = { ...this.preciosPos };
        delete precios[p.producto_id];
        delete precios[String(p.producto_id)];
        this.preciosPos = precios;
      } else {
        copy[p.producto_id] = next;
      }
      this.cantidades = copy;
    },
    onProdCatalogClick(p, e) {
      if (e && e.target && e.target.closest && e.target.closest(".pos-prod__pin")) return;
      if (this.sinStock(p)) return;
      this.agregar(p);
    },
    agregar(p) {
      this.salirDeCobroPorNuevaCarga();
      if (!this.productoVisibleEnPos(p)) {
        Swal.fire({
          icon: "info",
          title: "Producto oculto del POS",
          text: "Este producto está marcado solo para stock interno. Activalo en Productos si querés venderlo.",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 2800,
        });
        return;
      }
      if (this.sinStock(p)) return;
      if (this.esConsultarPrecio(p) && this.precioVentaProducto(p) == null) {
        this.abrirModalPrecioProducto(p);
        return;
      }
      this.add(p, 1);
    },
    quitar(p) {
      const copy = { ...this.cantidades };
      delete copy[p.producto_id];
      this.cantidades = copy;
      const precios = { ...this.preciosPos };
      delete precios[p.producto_id];
      delete precios[String(p.producto_id)];
      this.preciosPos = precios;
    },
    abrirModalPrecioProducto(p) {
      if (!p) return;
      this.salirDeCobroPorNuevaCarga();
      this.formPrecio = {
        productoId: p.producto_id,
        nombre: p.producto_nombre || "Producto",
        precio: null,
        cantidad: 1,
      };
      if (this.modalPrecio) this.modalPrecio.show();
    },
    onModalPrecioShown() {
      this.modalPrecioAbierto = true;
      this.$nextTick(() => {
        const el = this.$refs.precioPosInput;
        if (el) el.focus();
      });
    },
    onModalPrecioHidden() {
      this.modalPrecioAbierto = false;
      this.formPrecio = {
        productoId: null,
        nombre: "",
        precio: null,
        cantidad: 1,
      };
      this.$nextTick(() => this.enfocarScan());
    },
    confirmarPrecioProducto() {
      if (!this.puedeConfirmarPrecioProducto) return;
      const p = this.productos.find(
        (x) => x.producto_id === this.formPrecio.productoId,
      );
      if (!p) {
        if (this.modalPrecio) this.modalPrecio.hide();
        return;
      }
      if (this.sinStock(p)) {
        Swal.fire({
          icon: "warning",
          title: "Sin stock",
          text: "No hay stock disponible para este producto.",
        });
        return;
      }
      const precio = Number(this.formPrecio.precio);
      const q = Math.max(1, Math.floor(Number(this.formPrecio.cantidad) || 1));
      this.preciosPos = { ...this.preciosPos, [p.producto_id]: precio };
      this.add(p, q);
      if (this.modalPrecio) this.modalPrecio.hide();
      if (this.esMobile) this.panelMobile = "catalog";
    },
    setCantidadLinea(row, value) {
      let next = Math.floor(Number(value));
      if (!Number.isFinite(next) || next < 0) next = 0;

      if (row.libre) {
        const idx = this.itemsLibres.findIndex((x) => x.id === row.libreId);
        if (idx < 0) return;
        if (next <= 0) {
          this.itemsLibres = this.itemsLibres.filter(
            (x) => x.id !== row.libreId,
          );
          return;
        }
        const copy = this.itemsLibres.slice();
        copy[idx] = { ...copy[idx], q: next };
        this.itemsLibres = copy;
        return;
      }

      const max = this.stockMaxProducto(row.p);
      if (max != null) next = Math.min(next, max);
      this.add(row.p, next - this.cant(row.p));
    },
    onCantidadLineaInput(row, event) {
      const raw = event && event.target ? event.target.value : row.q;
      this.setCantidadLinea(row, raw);
      this.$nextTick(() => {
        if (!event || !event.target) return;
        if (row.libre) {
          const item = this.itemsLibres.find((x) => x.id === row.libreId);
          event.target.value = item ? item.q : 0;
          return;
        }
        event.target.value = this.cant(row.p);
      });
    },
    cambiarCantidadLinea(row, delta) {
      if (row.libre) {
        const idx = this.itemsLibres.findIndex((x) => x.id === row.libreId);
        if (idx < 0) return;
        const next = Math.max(
          0,
          Math.floor(Number(this.itemsLibres[idx].q) || 0) + delta,
        );
        if (next <= 0) {
          this.itemsLibres = this.itemsLibres.filter(
            (x) => x.id !== row.libreId,
          );
          return;
        }
        const copy = this.itemsLibres.slice();
        copy[idx] = { ...copy[idx], q: next };
        this.itemsLibres = copy;
        return;
      }
      this.add(row.p, delta);
    },
    quitarLinea(row) {
      if (row.libre) {
        this.itemsLibres = this.itemsLibres.filter((x) => x.id !== row.libreId);
        return;
      }
      this.quitar(row.p);
    },
    onModalLibreShown() {
      this.modalLibreAbierto = true;
      this.$nextTick(() => {
        const el = this.$refs.libreNombreInput;
        if (el) el.focus();
      });
    },
    onModalLibreHidden() {
      this.modalLibreAbierto = false;
      this.$nextTick(() => this.enfocarScan());
    },
    abrirModalLibre() {
      this.salirDeCobroPorNuevaCarga();
      this.formLibre = { nombre: "", precio: null, cantidad: 1 };
      if (this.modalLibre) this.modalLibre.show();
    },
    confirmarCobroLibre() {
      if (!this.puedeAgregarLibre) return;
      const precio = Number(this.formLibre.precio);
      const q = Math.max(1, Math.floor(Number(this.formLibre.cantidad) || 1));
      const nombre =
        String(this.formLibre.nombre || "").trim() || "Cobro libre";
      this.itemsLibres = [
        ...this.itemsLibres,
        {
          id: this.libreSeq++,
          nombre: nombre.slice(0, 120),
          precio,
          q,
        },
      ];
      if (this.modalLibre) this.modalLibre.hide();
      if (this.esMobile) this.panelMobile = "catalog";
      this.formLibre = { nombre: "", precio: null, cantidad: 1 };
    },
    limpiarBusqueda() {
      this.scan = "";
      this.enfocarScan();
    },
    async confirmarVaciar() {
      if (!this.lineas.length && !this.scan.trim()) return;
      const result = await Swal.fire({
        icon: "question",
        title: "¿Vaciar la venta?",
        text: "Se quitarán todos los productos y se limpiará la búsqueda.",
        showCancelButton: true,
        cancelButtonText: "Cancelar",
        confirmButtonText: "Sí, vaciar",
        confirmButtonColor: "#dc3545",
      });
      if (!result.isConfirmed) return;
      this.vaciar();
    },
    vaciar() {
      this.cantidades = {};
      this.preciosPos = {};
      this.itemsLibres = [];
      this.scan = "";
      limpiarPosDraft(this.usuario);
      this.enfocarScan();
    },
    async procesarScan() {
      const code = this.scan.trim();
      if (!code) return;
      const porCodigo = this.productos.find(
        (p) =>
          this.productoVisibleEnPos(p) &&
          p.producto_codigo_barras &&
          String(p.producto_codigo_barras) === code,
      );
      if (porCodigo) {
        this.agregar(porCodigo);
        this.scan = "";
        this.codigoInexistente = "";
        return;
      }
      const matches = this.productosFiltrados;
      if (matches.length === 1) {
        this.agregar(matches[0]);
        this.scan = "";
        this.codigoInexistente = "";
        return;
      }

      if (!this.esPosibleCodigoBarras(code)) return;

      // Sin match local: intentar catálogo compartido.
      if (this.adoptandoCatalogo) return;
      await this.intentarAdoptarDesdeCatalogo(code);
    },
    async intentarAdoptarDesdeCatalogo(code) {
      this.adoptandoCatalogo = true;
      try {
        const { item, fromCache } = await buscarCatalogoPorCodigo(code);
        if (!item) {
          this.mostrarProductoInexistente(code);
          return;
        }

        if (!isOnline()) {
          Swal.fire({
            icon: "warning",
            title: "Sin conexión",
            html: `<p class="mb-1"><strong>${this.escapeHtml(item.nombre)}</strong> está en el catálogo.</p>
                   <p class="mb-0 small text-muted">Conectate para adoptarlo a tu inventario.</p>`,
            confirmButtonText: "Entendido",
          });
          return;
        }

        const confirm = await Swal.fire({
          icon: "question",
          title: "Producto del catálogo",
          html: `<p class="mb-1"><strong>${this.escapeHtml(item.nombre)}</strong></p>
                 <p class="mb-0 small text-muted">${this.escapeHtml(item.categoria || "Sin categoría")} · ${this.escapeHtml(item.codigo_barras)}</p>
                 <p class="mt-2 mb-0 small">Se agregará a tu inventario como «Consultar precio» (oculto del menú online).</p>`,
          showCancelButton: true,
          confirmButtonText: "Adoptar y vender",
          cancelButtonText: "Cancelar",
          reverseButtons: true,
        });
        if (!confirm.isConfirmed) return;

        const data = await adoptarDesdeCatalogo({
          catalogoId: item.catalogo_id,
          codigoBarras: item.codigo_barras,
          usuarioNegocio: this.usuario,
        });
        const producto = data && data.producto;
        if (!producto) {
          throw new Error("No se recibió el producto adoptado.");
        }

        // Incorporar al catálogo local del POS.
        const ya = this.productos.some(
          (p) => String(p.producto_id) === String(producto.producto_id),
        );
        if (!ya) {
          this.productos = sortProductosEstables([
            ...this.productos,
            producto,
          ]);
          try {
            await cachePosCatalog(`catalogo:${this.usuario}`, this.productos);
          } catch (_) {
            /* ignore */
          }
        }

        this.scan = "";
        this.agregar(producto);
        if (fromCache) {
          /* no-op: info only used for offline path */
        }
      } catch (e) {
        console.warn("[pos catalogo]", e);
        Swal.fire({
          icon: "error",
          title: "No se pudo adoptar",
          text:
            (e.response && e.response.data && e.response.data.message) ||
            e.message ||
            "Intentá de nuevo.",
        });
      } finally {
        this.adoptandoCatalogo = false;
        this.enfocarScan();
      }
    },
    escapeHtml(value) {
      return String(value == null ? "" : value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    },
    esPosibleCodigoBarras(valor) {
      return /^\d{4,64}$/.test(String(valor || "").trim());
    },
    mostrarProductoInexistente(code) {
      this.codigoInexistente = String(code || "").trim();
    },
    resetFormNuevoProducto(codigo = "") {
      this.formNuevoProducto = {
        nombre: "",
        categoria: "",
        precio: null,
        codigo_barras: String(codigo || "").trim().slice(0, 64),
      };
      this.mostrarAltaCategoriaPos = false;
      this.nuevaCategoriaNombre = "";
    },
    onEnterFormNuevoProducto(e) {
      const t = e.target;
      if (t && t.tagName === "BUTTON") return;
      if (t && typeof t.closest === "function" && t.closest("[data-pos-cat-alta]")) {
        e.preventDefault();
        this.agregarCategoriaPos();
        return;
      }
      e.preventDefault();
    },
    async cargarCategoriasPos() {
      const usuario = this.usuario;
      let cargadoDesdeApi = false;
      if (isOnline()) {
        try {
          const { data } = await axios.get(`/categorias?usuario=${usuario}`);
          if (Array.isArray(data)) {
            this.categorias = data;
            await cacheCategorias(`categorias:${usuario}`, data);
            cargadoDesdeApi = true;
          }
        } catch (error) {
          console.error("Error obteniendo categorías:", error);
        }
      }
      if (!cargadoDesdeApi) {
        const cache = await obtenerCategoriasCatalogo(usuario);
        this.categorias =
          cache && cache.length > 0
            ? cache
            : [{ id: "varios", categoria_nombre: "Varios" }];
      }
    },
    async abrirModalNuevoProducto() {
      const codigo = this.codigoInexistente || this.scan.trim();
      this.resetFormNuevoProducto(codigo);
      await this.cargarCategoriasPos();
      if (
        !this.formNuevoProducto.categoria &&
        this.categorias.length === 1 &&
        this.categorias[0] &&
        this.categorias[0].categoria_nombre
      ) {
        this.formNuevoProducto.categoria = this.categorias[0].categoria_nombre;
      }
      if (this.modalNuevoProducto) this.modalNuevoProducto.show();
    },
    onModalNuevoProductoShown() {
      this.modalNuevoProductoAbierto = true;
      this.$nextTick(() => {
        const el = this.$refs.nuevoProdNombreInput;
        if (el) el.focus();
      });
    },
    onModalNuevoProductoHidden() {
      this.modalNuevoProductoAbierto = false;
      this.mostrarAltaCategoriaPos = false;
      this.nuevaCategoriaNombre = "";
      this.$nextTick(() => this.enfocarScan());
    },
    liberarModalBootstrap(instance) {
      if (instance) {
        try {
          instance.dispose();
        } catch (_) {
          /* el DOM ya pudo desmontarse */
        }
      }
      document.querySelectorAll(".modal-backdrop").forEach((n) => n.remove());
      document.body.classList.remove("modal-open");
      document.body.style.removeProperty("overflow");
      document.body.style.removeProperty("padding-right");
      document.body.style.removeProperty("padding-left");
    },
    irANuevoProductoCompleto() {
      const codigo = String(
        this.formNuevoProducto.codigo_barras || this.codigoInexistente || "",
      )
        .trim()
        .slice(0, 64);
      const instance = this.modalNuevoProducto;
      this.modalNuevoProducto = null;
      this.modalNuevoProductoAbierto = false;
      this.liberarModalBootstrap(instance);
      this.$router.push({
        path: "/u/nuevoProducto",
        query: codigo ? { codigo } : {},
      });
    },
    async agregarCategoriaPos() {
      if (!this.puedeAgregarCategoriaPos || this.guardandoCategoriaPos) return;
      const nombreCat = String(this.nuevaCategoriaNombre || "").trim();
      if (!nombreCat) return;
      this.guardandoCategoriaPos = true;
      try {
        if (isOnline()) {
          const { data } = await axios.post("/asegurarCategoria", {
            categoria_nombre: nombreCat,
            usuario_nombre: this.usuario,
          });
          await this.cargarCategoriasPos();
          this.formNuevoProducto.categoria =
            (data && data.categoria_nombre) || nombreCat;
        } else {
          await agregarCategoriaOffline(this.usuario, {
            categoria_nombre: nombreCat,
            usuario_nombre: this.usuario,
          });
          this.categorias = await obtenerCategoriasCatalogo(this.usuario);
          this.formNuevoProducto.categoria = nombreCat;
        }
        this.nuevaCategoriaNombre = "";
        this.mostrarAltaCategoriaPos = false;
      } catch (error) {
        console.error("Error al agregar categoría:", error);
        Swal.fire({
          icon: "error",
          text: "No se pudo agregar la categoría.",
        });
      } finally {
        this.guardandoCategoriaPos = false;
      }
    },
    async incorporarProductoAlPos(producto, codigoBarras) {
      if (!producto) return null;
      const ya = this.productos.some(
        (p) => String(p.producto_id) === String(producto.producto_id),
      );
      if (!ya) {
        this.productos = sortProductosEstables([...this.productos, producto]);
        try {
          await cachePosCatalog(`catalogo:${this.usuario}`, this.productos);
        } catch (_) {
          /* ignore */
        }
      }
      const creado =
        this.productos.find(
          (p) => String(p.producto_id) === String(producto.producto_id),
        ) ||
        this.productos.find(
          (p) =>
            codigoBarras &&
            String(p.producto_codigo_barras || "").trim() ===
              String(codigoBarras).trim(),
        );
      return creado || producto;
    },
    async recargarProductosPos() {
      if (!isOnline()) {
        const cacheLocal =
          (await getCachedPosCatalog(`catalogo:${this.usuario}`)) || [];
        const outbox = (await idbGetAll("outbox_productos")) || [];
        const misPendientes = outbox.filter((p) =>
          sameUsuarioOffline(p.usuario, this.usuario),
        );
        this.productos = mergeProductosCatalogo(cacheLocal, misPendientes);
        return;
      }
      try {
        const { data } = await axios.get("/productos", {
          params: { usuario: this.usuario },
        });
        if (Array.isArray(data)) {
          this.productos = mergeProductosCatalogo(data, []);
          await cachePosCatalog(`catalogo:${this.usuario}`, this.productos);
        }
      } catch (e) {
        console.warn("[pos recargar productos]", e);
      }
      void this.cargarRankingVentas();
    },
    async guardarProductoNuevoPos() {
      if (!this.puedeGuardarProductoNuevo || this.guardandoProductoNuevo) return;
      const form = this.$refs.formNuevoProductoPos;
      if (form && typeof form.reportValidity === "function" && !form.reportValidity()) {
        return;
      }
      const nombre = String(this.formNuevoProducto.nombre || "").trim();
      const categoria = String(this.formNuevoProducto.categoria || "").trim();
      const precio = Number(this.formNuevoProducto.precio);
      const codigoBarras = String(this.formNuevoProducto.codigo_barras || "")
        .trim()
        .slice(0, 64);
      if (!nombre || !categoria || !Number.isFinite(precio)) {
        Swal.fire({
          icon: "error",
          text: "Completá nombre, categoría y precio.",
        });
        return;
      }

      const payloadProducto = {
        nombre,
        descripcion: "",
        categoria,
        precio,
        stock: -1,
        codigo_barras: codigoBarras || undefined,
        producto_codigo_barras: codigoBarras || undefined,
        en_pos: true,
        producto_en_pos: 1,
        usuario: this.usuario,
      };

      this.guardandoProductoNuevo = true;
      try {
        let creado = null;
        if (isOnline()) {
          const { data } = await axios.post("/nuevoProducto", {
            producto: payloadProducto,
          });
          await this.recargarProductosPos();
          const pid = data && data.producto_id;
          creado =
            (pid != null &&
              this.productos.find(
                (p) => String(p.producto_id) === String(pid),
              )) ||
            this.productos.find(
              (p) =>
                codigoBarras &&
                String(p.producto_codigo_barras || "").trim() === codigoBarras,
            );
          if (!creado && pid != null) {
            creado = await this.incorporarProductoAlPos(
              {
                producto_id: pid,
                producto_nombre: nombre,
                producto_precio: precio,
                producto_categoria: categoria,
                producto_codigo_barras: codigoBarras || null,
                producto_stock: -1,
                producto_en_pos: 1,
                producto_disponibilidad: 1,
              },
              codigoBarras,
            );
          }
        } else {
          const pendiente = await guardarProductoOffline(
            this.usuario,
            payloadProducto,
          );
          creado = await this.incorporarProductoAlPos(pendiente, codigoBarras);
        }

        if (this.modalNuevoProducto) this.modalNuevoProducto.hide();
        this.codigoInexistente = "";
        this.scan = "";
        if (creado) this.agregar(creado);
        Swal.fire({
          icon: isOnline() ? "success" : "warning",
          title: isOnline()
            ? "Producto agregado"
            : "Guardado de manera local (Offline)",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 2200,
        });
      } catch (error) {
        console.error("Error al guardar el producto:", error);
        Swal.fire({
          icon: "error",
          text:
            (error.response &&
              error.response.data &&
              error.response.data.message) ||
            "No se pudo guardar el producto.",
        });
      } finally {
        this.guardandoProductoNuevo = false;
      }
    },
    async cargarCaja() {
      const st = await resolveEstadoCaja(this.usuario);
      this.cajaRequerida = st.requerida;
      this.cajaAbierta = st.abierta;
      this.cajaId = st.cajaId;
    },
    async cargar() {
      try {
        // === 1. MODO OFFLINE ===
        if (!isOnline()) {
          const cacheLocal =
            (await getCachedPosCatalog(`catalogo:${this.usuario}`)) || [];
          const outbox = (await idbGetAll("outbox_productos")) || [];
          const misPendientes = outbox.filter((p) =>
            sameUsuarioOffline(p.usuario, this.usuario),
          );

          this.productos = mergeProductosCatalogo(cacheLocal, misPendientes);
          this.ticketCfg =
            (await getCachedPosSnapshot(`ticket:${this.usuario}`)) || {};
          this.negocio =
            (await getCachedPosSnapshot(`negocio:${this.usuario}`)) || {};
          this.clientes =
            (await getCachedPosSnapshot(`clientes:${this.usuario}`)) || [];

          await this.cargarCaja();
          await this.restaurarPosDraft();
          await this.cargarRankingVentas();
          return;
        }

        // === 2. MODO ONLINE ===
        const [pRes, tRes, nRes, cRes] = await Promise.allSettled([
          axios.get("/productos", { params: { usuario: this.usuario } }),
          axios.get("/ticket-config"),
          axios.get(`/miNegocio?usuario=${this.usuario}`),
          axios.get("/clientes", { params: { usuario: this.usuario } }),
        ]);

        // Productos
        if (pRes.status === "fulfilled" && Array.isArray(pRes.value.data)) {
          this.productos = mergeProductosCatalogo(pRes.value.data, []);
          await cachePosCatalog(`catalogo:${this.usuario}`, this.productos);
        } else {
          const cacheLocal =
            (await getCachedPosCatalog(`catalogo:${this.usuario}`)) || [];
          const outbox = (await idbGetAll("outbox_productos")) || [];
          const misPendientes = outbox.filter((p) =>
            sameUsuarioOffline(p.usuario, this.usuario),
          );

          this.productos = mergeProductosCatalogo(cacheLocal, misPendientes);
        }

        void this.cargarRankingVentas();

        // Configuración del Ticket
        if (tRes.status === "fulfilled") {
          this.ticketCfg = tRes.value.data || {};
          await cachePosSnapshot(`ticket:${this.usuario}`, this.ticketCfg);
        } else {
          this.ticketCfg =
            (await getCachedPosSnapshot(`ticket:${this.usuario}`)) || {};
        }

        // Negocio
        if (nRes.status === "fulfilled") {
          this.negocio = nRes.value.data || {};
          await cachePosSnapshot(`negocio:${this.usuario}`, this.negocio);
        } else {
          this.negocio =
            (await getCachedPosSnapshot(`negocio:${this.usuario}`)) || {};
        }

        // Clientes
        if (cRes.status === "fulfilled" && Array.isArray(cRes.value.data)) {
          this.clientes = cRes.value.data;
          await cachePosSnapshot(`clientes:${this.usuario}`, this.clientes);
        } else {
          this.clientes =
            (await getCachedPosSnapshot(`clientes:${this.usuario}`)) || [];
        }

        await this.cargarCaja();
        await this.restaurarPosDraft();
      } catch (e) {
        console.error("Error crítico al cargar:", e);
        Swal.fire({
          icon: "error",
          title: "No se pudieron cargar los datos",
        });
      }
    },
    async aplicarStockTrasVenta(lineas, { offline = false } = {}) {
      const cambios = (lineas || [])
        .filter((row) => !row.libre && row.p?.producto_id)
        .map((row) => ({
          producto_id: row.p.producto_id,
          qty: Number(row.q || 0),
          producto_nombre: row.p.producto_nombre,
        }));
      if (!cambios.length) return;
      this.productos = await aplicarStockLocalCatalogo(this.usuario, cambios, {
        motivo: "venta_pos",
        detalle: "Venta POS",
        registrarMovimiento: !!offline,
      });
    },
    resetTrasCobroExitoso() {
      this.cantidades = {};
      this.preciosPos = {};
      this.itemsLibres = [];
      this.scan = "";
      limpiarPosDraft(this.usuario);
      if (this.esMobile) {
        this.panelMobile = "catalog";
      }
      this.cerrarCobro();
    },
    async abrirCobro() {
      if (!this.lineas.length) return;
      await this.cargarCaja();
      if (this.cajaRequerida && !this.cajaAbierta) {
        this.abrirModalCaja(false);
        return;
      }
      this.recibido = null;
      this.medioPago = "Efectivo";
      this.resetClienteCobro();
      if (this.esMobile) {
        this.panelMobile = "venta";
      }
      this.modoCobro = true;
    },
    nombreVendedor() {
      return getVendedorNombre() || getNombre() || getLogin() || "";
    },
    /** Toast del POS: solo ícono, sin texto. */
    posToast({ icon = "success", timer = 1100, ariaLabel = "Listo" } = {}) {
      const iconHtml = {
        success: '<i class="bi bi-check-lg" aria-hidden="true"></i>',
        info: '<i class="bi bi-cloud-slash" aria-hidden="true"></i>',
        warning: '<i class="bi bi-exclamation-lg" aria-hidden="true"></i>',
      };
      return Swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer,
        timerProgressBar: false,
        customClass: {
          popup: "pos-toast",
          icon: "pos-toast__icon",
        },
      }).fire({
        iconHtml: iconHtml[icon] || iconHtml.success,
        title: "\u200b",
        didOpen: (el) => {
          el.setAttribute("aria-label", ariaLabel);
        },
      });
    },
    async cobrar() {
      await this.cargarCaja();
      if (this.offline.upgradeRequired) {
        return Swal.fire({
          icon: "error",
          title: "Actualización requerida",
          text: "Actualizá el POS antes de crear ventas nuevas.",
        });
      }
      if (!isOnline() && (this.esMedioQr || this.esMedioPoint)) {
        return Swal.fire({
          icon: "info",
          title: "Mercado Pago requiere internet",
        });
      }
      if (this.cajaRequerida && !this.cajaAbierta) {
        this.cerrarCobro();
        this.abrirModalCaja(false);
        return;
      }
      this.cobrando = true;
      const items = this.lineas.map((row) => ({
        producto_id: row.libre ? null : row.p.producto_id,
        producto_nombre: row.p.producto_nombre,
        producto_precio: row.precioUnitario,
        producto_categoria: row.p.producto_categoria,
        cantidadSeleccionada: row.q,
      }));
      const medioCobro = this.medioPagoEfectivoCobro();
      const ventaSnapshot = {
        items: this.lineas.map((r) => ({
          nombre: r.p.producto_nombre,
          q: r.q,
          precio: Number(r.precioUnitario),
          subtotal: r.subtotal,
          ivaAlicuota: r.p && (r.p.iva_alicuota != null ? r.p.iva_alicuota : r.p.ivaAlicuota),
          impuestosInternos: r.p && (r.p.impuestos_internos != null ? r.p.impuestos_internos : r.p.impuestosInternos),
        })),
        total: this.total,
        medioPago: medioCobro,
        recibido:
          this.medioPago === "Efectivo" ? Number(this.recibido || 0) : null,
        vuelto: this.medioPago === "Efectivo" ? Math.max(0, this.vuelto) : null,
        vendedor: this.nombreVendedor(),
        fecha: new Date(),
      };
      try {
        const dev = await getDeviceRecord(this.usuario);
        if (!dev || !dev.device_uuid || !dev.codigo) {
          throw new Error(
            "Este POS todavía no está enrolado. Conectate a internet para configurarlo.",
          );
        }
        const pedidoBody = {
          usuario_negocio: this.usuario,
          cliente_nombre: this.clienteNombre || "Consumidor final",
          cliente_id: this.clienteId || null,
          medio_pago: medioCobro,
          direccion_entrega: null,
          detalle_cliente: "Venta POS",
          total: this.total,
          items,
          origen: "pos",
          descontar_stock: true,
          vendedor: this.nombreVendedor() || null,
          expected_caja_id: this.cajaId || null,
          schema_version: SCHEMA_VERSION,
          app_version: APP_VERSION,
          device_uuid: dev.device_uuid,
          server_timestamp: new Date(
            Date.now() + (this.offline.serverOffsetMs || 0),
          ).toISOString(),
        };
        if (this.esMedioPoint && this.mpPoint.orderId) {
          pedidoBody.mp_point_order_id = this.mpPoint.orderId;
        }
        if (this.deviceBlocked) {
          Swal.fire({
            icon: "error",
            title: "Dispositivo revocado",
            text: "Contactá al administrador o exportá el respaldo de ventas pendientes.",
          });
          return;
        }
        if (!isOnline()) {
          const mutationId = generateUuid();
          const queued = await enqueueOfflineSale(
            {
              ...pedidoBody,
              client_mutation_id: mutationId,
            },
            dev,
          );

          await this.aplicarStockTrasVenta(this.lineas, { offline: true });
          ventaSnapshot.ticketFolio = queued.ticket_folio;
          this.offline.pending = await countPendingOutbox();
          this.resetTrasCobroExitoso();
          this.posToast({ icon: "info", ariaLabel: "Venta guardada offline" });
          await this.cargarCaja();
          this.enfocarScan();
          if (this.imprimir) {
            void this.finalizarImpresionOffline(ventaSnapshot, mutationId);
          }
        } else {
          const { data: pedidoRes } = await axios.post("/pedidos", {
            ...pedidoBody,
            client_mutation_id: generateUuid(),
          });
          await this.aplicarStockTrasVenta(this.lineas, { offline: false });
          const pedidoId = pedidoRes && pedidoRes.pedido_id;
          const totalVenta = Number(ventaSnapshot.total);
          const clienteNombre =
            this.clienteNombre || "Consumidor final";
          const quiereFacturar =
            this.puedeFacturarArca && this.facturarArca && this.offline.online;
          const quiereImprimir = this.imprimir;
          ventaSnapshot.clienteNombre = clienteNombre;
          this.resetTrasCobroExitoso();
          this.posToast({ ariaLabel: "Venta cobrada" });
          if (quiereFacturar && pedidoId) {
            void this.emitirFacturaArcaPos({
              pedido_id: pedidoId,
              imp_total: totalVenta,
              receptor_nombre: clienteNombre,
              venta: ventaSnapshot,
              imprimir: quiereImprimir,
            });
          } else if (quiereImprimir) {
            void this.imprimirTicket(ventaSnapshot);
          }
          await this.cargarCaja();
          this.enfocarScan();
        }
      } catch (e) {
        const data = e.response && e.response.data;
        if (data && data.code === "SIN_CAJA_ABIERTA") {
          await this.cargarCaja();
          this.cerrarCobro();
          if (!this.cajaAbierta) this.abrirModalCaja(false);
          return;
        }
        const msg = data && data.message;
        Swal.fire({
          icon: "error",
          title: "No se pudo cobrar",
          text: msg || "",
        });
      } finally {
        this.cobrando = false;
      }
    },
        async finalizarImpresionOffline(venta, mutationId) {
            const printed = await this.imprimirTicket(venta);
            if (printed && mutationId) {
                await updateOutboxEntry(mutationId, {
                    printed: true,
                    printed_at: new Date().toISOString(),
                });
            }
        },
        async imprimirTicket(venta) {
            try {
                await printSaleTicket({
                    venta,
                    ticketCfg: this.ticketCfg,
                    negocio: this.negocio,
                    arca: this.arcaEmisor || {},
                });
                return true;
            } catch (e) {
                const msg =
                    e.code === 'NO_PRINT' || e.code === 'PRINTIFY_DOWN' || e.code === 'PRINTIFY_TIMEOUT'
                        ? printingHint(this.ticketCfg)
                        : (e && e.message) || 'No se pudo imprimir el ticket.';
                Swal.fire({ icon: 'warning', title: 'Impresión', text: msg });
                return false;
            }
        },
        async imprimirTicketFiscal(venta, comprobante) {
            try {
                await printFiscalSaleTicket({
                    venta,
                    ticketCfg: this.ticketCfg,
                    negocio: this.negocio,
                    arca: this.arcaEmisor || {},
                    comprobante,
                });
                return true;
            } catch (e) {
                const msg =
                    e.code === 'NO_PRINT' || e.code === 'PRINTIFY_DOWN' || e.code === 'PRINTIFY_TIMEOUT'
                        ? printingHint(this.ticketCfg)
                        : (e && e.message) || 'No se pudo imprimir el comprobante fiscal.';
                Swal.fire({ icon: 'warning', title: 'Impresión fiscal', text: msg });
                return false;
            }
        },
        async cargarEstadoArca() {
            if (!hasFeature('integracion_arca')) {
                this.arcaListo = false;
                this.arcaEmisor = null;
                return;
            }
            this.arcaCargando = true;
            try {
                const { data } = await axios.get('/arca/estado');
                this.arcaListo = !!data.listo_para_facturar;
                this.facturarArca = !!(data.defaults && data.defaults.pos_facturar);
                this.arcaEmisor = data.vinculado
                    ? {
                        cuit: data.cuit || null,
                        cuit_formato: data.cuit_formato || null,
                        razon_social: data.razon_social || null,
                        condicion_iva: data.condicion_iva || null,
                        punto_venta: data.punto_venta != null ? Number(data.punto_venta) : null,
                        defaults: data.defaults || null,
                        ambiente: data.ambiente || null,
                    }
                    : null;
            } catch {
                this.arcaListo = false;
                this.facturarArca = false;
                this.arcaEmisor = null;
            } finally {
                this.arcaCargando = false;
            }
        },
        async emitirFacturaArcaPos({ pedido_id, imp_total, receptor_nombre, venta, imprimir }) {
            try {
                const { data } = await axios.post('/arca/comprobantes/emitir', {
                    pedido_id,
                    imp_total,
                    receptor_nombre,
                });
                this.posToast({ ariaLabel: "Factura emitida", timer: 1400 });
                if (imprimir && data && data.cae) {
                    await this.imprimirTicketFiscal(venta, data);
                } else if (imprimir) {
                    await this.imprimirTicket(venta);
                }
                return data;
            } catch (e) {
                const msg =
                    (e.response && e.response.data && e.response.data.message) ||
                    'La venta se cobró, pero no se pudo facturar en ARCA.';
                Swal.fire({
                    icon: 'warning',
                    title: 'Factura ARCA',
                    text: msg,
                });
                if (imprimir) {
                    await this.imprimirTicket(venta);
                }
                return null;
            }
        },
  },
};
</script>

<style scoped>
.pos-page {
  background: #f1f5f9;
  height: 100dvh;
  max-height: 100dvh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

.pos-shell {
  display: grid;
  grid-template-columns: minmax(0, 1fr) min(480px, 38vw);
  gap: 0;
  flex: 1;
  min-height: 0;
  max-height: 100%;
  overflow: hidden;
  max-width: none;
  width: 100%;
  margin: 0;
  padding: 0;
  align-items: stretch;
}

.pos-shell > * {
  min-height: 0;
}

.pos-catalog {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    align-self: stretch;
    width: 100%;
    height: 100%;
    min-height: 0;
    padding: 0.75rem 0.85rem 0.65rem;
}

.pos-page--fs {
  background: #f1f5f9;
}

.pos-scan {
    position: relative;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
}

.pos-scan__field {
  position: relative;
  flex: 1;
  min-width: 0;
}

.pos-scan__ico {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 1.3rem;
  color: #64748b;
  pointer-events: none;
  z-index: 1;
}

.pos-scan__input {
  padding-left: 44px;
  border-radius: 14px;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.06);
}

.pos-scan__clear {
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
}

.pos-scan__fs {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  padding: 0;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.06);
}

.pos-libre-btn {
  font-weight: 600;
}

.pos-cats {
    display: flex;
    gap: 0.4rem;
    flex-wrap: wrap;
    flex-shrink: 0;
}

.pos-inexistente {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-shrink: 0;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 14px;
  padding: 0.85rem 1rem;
}

.pos-inexistente__info {
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;
  min-width: 0;
}

.pos-inexistente__info > i {
  font-size: 1.25rem;
  color: #ea580c;
  margin-top: 0.1rem;
}

.pos-inexistente__info strong {
  display: block;
  color: #9a3412;
  font-size: 0.95rem;
}

.pos-inexistente__info p {
  color: #9a3412;
  font-size: 0.82rem;
}

.pos-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    grid-auto-rows: min-content;
    gap: 0.5rem;
    align-items: start;
    align-content: start;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    padding-top: 0.35rem;
    padding-right: 0.2rem;
    padding-bottom: 0.5rem;
    padding-left: 0.2rem;
    margin-left: -0.2rem;
    margin-right: -0.2rem;
}

.pos-prod {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 0.15rem;
    text-align: left;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 0.5rem 0.65rem;
    height: auto;
    min-height: 0;
    cursor: pointer;
    transition: transform 0.12s ease, box-shadow 0.12s ease, border-color 0.12s ease;
}

.pos-prod:hover:not(.pos-prod--sinstock) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.1);
  border-color: #93c5fd;
}

.pos-prod--sinstock {
    background: #f1f5f9;
    border-color: #e2e8f0;
    opacity: 0.72;
    cursor: default;
}

.pos-prod--pinned {
  border-color: #93c5fd;
  background: #f8fbff;
}

.pos-prod__head {
  display: flex;
  align-items: flex-start;
  gap: 0.15rem;
}

.pos-prod__name {
    flex: 1;
    min-width: 0;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: normal;
    word-break: break-word;
    font-weight: 600;
    font-size: 0.85rem;
    line-height: 1.35;
    min-height: calc(2 * 1.35em + 0.12em);
    padding-bottom: 0.12em;
}

.pos-prod__pin {
  flex-shrink: 0;
  width: 1.35rem;
  height: 1.35rem;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #94a3b8;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.pos-prod__pin:hover,
.pos-prod__pin.is-on {
  color: #2563eb;
  background: #eff6ff;
}

.pos-prod__price {
  font-weight: 700;
  color: #0d9488;
}

.pos-prod__price--consultar {
  font-weight: 600;
  color: #64748b;
  font-size: 0.78rem;
}

.pos-prod--consultar {
  border-style: dashed;
}

.pos-prod__stock {
  font-size: 0.7rem;
  color: #94a3b8;
}

.pos-prod__stock-badge {
    align-self: flex-start;
    margin-top: 0.15rem;
    padding: 0.15rem 0.5rem;
    border-radius: 999px;
    background: rgba(220, 38, 38, 0.1);
    color: #dc2626;
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.01em;
}

.pos-cart {
  position: relative;
  align-self: stretch;
  height: 100%;
  max-height: 100%;
  min-height: 0;
  min-width: 0;
  width: 100%;
  background: #fff;
  border-radius: 0;
  border-left: 1px solid #e2e8f0;
  box-shadow: none;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.pos-cart__body {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.pos-cart__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.1rem 0.5rem;
  flex-shrink: 0;
  gap: 0.5rem;
}

.pos-cart--checkout .pos-cart__head {
  padding-top: 0.75rem;
  padding-bottom: 0.35rem;
}

.pos-cart__back {
  flex-shrink: 0;
  min-width: 4.5rem;
  font-weight: 600;
}

.pos-cart__head-spacer {
  flex-shrink: 0;
  min-width: 4.5rem;
}

.pos-cart__checkout-total {
  font-size: 1.35rem;
  font-weight: 800;
  color: #15803d;
  line-height: 1.1;
}

.pos-caja-badge {
  font-size: 0.68rem;
  font-weight: 600;
  padding: 0.28rem 0.55rem;
  color: #15803d;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
}

.pos-cart__empty {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 2rem 1rem;
  text-align: center;
}

.pos-lines {
  list-style: none;
  margin: 0;
  padding: 0 0.6rem;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.pos-line {
  --pos-line-qty-w: 8.35rem;
  --pos-line-total-w: 7.25rem;
  --pos-line-del-w: 1.75rem;
  display: grid;
  grid-template-columns:
    minmax(0, 1fr)
    var(--pos-line-qty-w)
    var(--pos-line-total-w)
    var(--pos-line-del-w);
  align-items: start;
  column-gap: 0.45rem;
  row-gap: 0.15rem;
  padding: 0.5rem 0.4rem;
  border-bottom: 1px solid #f1f5f9;
}

.pos-line__info {
  min-width: 0;
  overflow: visible;
}

.pos-line__name {
  font-weight: 600;
  font-size: 0.85rem;
  line-height: 1.25;
  min-width: 0;
}

.pos-line__name-text {
  overflow-wrap: break-word;
  word-break: break-word;
}

.pos-line__libre {
  display: inline-block;
  vertical-align: middle;
  margin-left: 0.3rem;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #0f172a;
  background: #e2e8f0;
  border-radius: 999px;
  padding: 0.05rem 0.4rem;
  line-height: 1.2;
}

.pos-line__sub {
  margin-top: 0.15rem;
  font-size: 0.72rem;
  line-height: 1.25;
  color: #94a3b8;
  overflow-wrap: break-word;
  word-break: break-word;
}

.pos-line__qty {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.3rem;
  width: var(--pos-line-qty-w);
  min-width: var(--pos-line-qty-w);
  max-width: var(--pos-line-qty-w);
  flex-shrink: 0;
  justify-self: end;
  margin-top: 0.05rem;
}

.pos-line__qty-btn {
  width: 1.85rem;
  height: 1.85rem;
  padding: 0;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.pos-line__qty-input {
  width: 3.15rem;
  min-width: 3.15rem;
  flex: 0 0 3.15rem;
  padding-left: 0.2rem;
  padding-right: 0.2rem;
  text-align: center;
  font-weight: 600;
  -moz-appearance: textfield;
}

.pos-line__qty-input::-webkit-outer-spin-button,
.pos-line__qty-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.pos-line__qty span {
  min-width: 1.4rem;
  text-align: center;
  font-weight: 600;
}

.pos-line__total {
  font-weight: 700;
  font-size: 0.85rem;
  line-height: 1.25;
  width: 100%;
  min-width: 0;
  text-align: right;
  font-variant-numeric: tabular-nums;
  overflow-wrap: break-word;
  word-break: break-word;
  margin-top: 0.05rem;
}

.pos-line__del {
  width: var(--pos-line-del-w);
  min-width: var(--pos-line-del-w);
  padding: 0;
  margin-top: 0.15rem;
  justify-self: end;
  line-height: 1;
}

.pos-cart__foot {
  padding: 0.75rem 1rem 0.85rem;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
  flex-shrink: 0;
}

.pos-cart__pay-wrap {
  margin-top: 0.5rem;
}

.pos-cart__pay-btn {
  --pos-pay-btn-h: 3rem;
  min-height: var(--pos-pay-btn-h);
  padding: 0 1rem;
  font-size: 1rem;
  font-weight: 650;
  line-height: 1.2;
}

.pos-cart__pay-btn-inner {
  display: grid;
  grid-template-columns: 1.25rem 1fr 3.25rem;
  align-items: center;
  gap: 0.4rem;
  width: 100%;
}

.pos-cart__pay-btn-ico {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  font-size: 1.1rem;
  line-height: 1;
}

.pos-cart__pay-btn-label {
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pos-kbd--on-btn {
  margin-left: 0;
  justify-self: end;
  min-width: 3rem;
  text-align: center;
  border-color: rgba(255, 255, 255, 0.45);
  background: rgba(255, 255, 255, 0.18);
}

.pos-cobro-opts {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem 1.25rem;
}

.pos-cobro-opts .form-check {
  min-height: 0;
  padding-left: 1.5em;
}

.pos-total {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  font-weight: 600;
}

.pos-total__num {
  font-size: 1.8rem;
  font-weight: 800;
}

.pos-pay-methods {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(90px, 1fr));
  gap: 0.5rem;
}

.pos-pay-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  padding: 0.65rem 0.4rem;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;
  font-size: 0.78rem;
  font-weight: 600;
  color: #475569;
  transition: all 0.12s ease;
}

.pos-pay-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  background: #f1f5f9;
  color: #94a3b8;
  transform: none;
  box-shadow: none;
}

.pos-pay-btn i {
  font-size: 1.25rem;
}

.pos-pay-btn.active {
  border-color: #16a34a;
  background: #f0fdf4;
  color: #15803d;
}

.pos-kbd {
  display: inline-block;
  margin-left: 0.35rem;
  padding: 0.08rem 0.38rem;
  border: 1px solid rgba(255, 255, 255, 0.45);
  border-radius: 0.35rem;
  background: rgba(255, 255, 255, 0.18);
  color: inherit;
  font-size: 0.68rem;
  font-weight: 700;
  line-height: 1.2;
  font-family: inherit;
  vertical-align: middle;
}

.pos-kbd--muted {
  margin-left: 0;
  border-color: #cbd5e1;
  background: #f8fafc;
  color: #64748b;
}

.pos-kbd--dark {
  margin-left: 0.2rem;
  border-color: #cbd5e1;
  background: #fff;
  color: #64748b;
}

.pos-kbd-hint {
  line-height: 1.5;
}

.pos-cart--checkout {
  box-shadow: 0 8px 28px rgba(22, 163, 74, 0.12);
  border: 1px solid rgba(22, 163, 74, 0.15);
}

.pos-checkout-panel {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding: 0 0.55rem 0.35rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.pos-checkout-panel--qr {
  justify-content: center;
}

.pos-checkout-methods {
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.35rem;
  margin-bottom: 0 !important;
}

.pos-checkout-methods .pos-pay-btn {
  flex-direction: row;
  justify-content: center;
  min-height: 36px;
  padding: 0.3rem 0.35rem;
  font-size: 0.7rem;
}

.pos-checkout-methods .pos-pay-btn i {
  font-size: 0.9rem;
}

.pos-checkout-cash {
  padding: 0.45rem 0.55rem 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;
}

.pos-checkout-cash__row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.65rem;
  align-items: end;
}

.pos-checkout-cash__vuelto {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: flex-end;
  min-width: 5rem;
  padding-bottom: 0.15rem;
}

.pos-checkout-cash__vuelto .fw-bold {
  font-size: 1.05rem;
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
}

.pos-checkout-bills {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-top: 0.4rem;
}

.pos-checkout-bills .btn {
  padding: 0.15rem 0.5rem;
  font-size: 0.72rem;
}

.pos-checkout-transfer {
  padding: 0.55rem 0.7rem;
  border: 1px solid rgba(52, 131, 250, 0.18);
  border-radius: 10px;
  background: linear-gradient(
    135deg,
    rgba(52, 131, 250, 0.06),
    rgba(14, 165, 233, 0.04)
  );
}

.pos-checkout-transfer .fw-semibold {
  margin-bottom: 0.4rem !important;
  font-size: 0.95rem;
}

.pos-transfer-box {
  display: grid;
  gap: 0.35rem;
  font-size: 0.95rem;
}

.pos-transfer-box div {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  align-items: baseline;
  color: #64748b;
}

.pos-transfer-box strong {
  color: #0f172a;
  text-align: right;
  word-break: break-word;
  font-size: 1.05rem;
  font-weight: 700;
}

.pos-checkout-qr {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
  border: 0;
  border-radius: 12px;
  padding: 0.15rem 0.25rem;
  background: transparent;
}

.pos-qr-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  min-height: 0;
  width: 100%;
  justify-content: center;
}

.pos-qr-amount {
  font-size: 1.45rem;
  font-weight: 800;
  color: #15803d;
  line-height: 1.15;
  margin-bottom: 0.4rem;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
}

.pos-qr-img {
  width: min(280px, 92%);
  height: auto;
  aspect-ratio: 1;
  max-height: min(280px, calc(100% - 7.25rem));
  object-fit: contain;
  border-radius: 12px;
  background: #fff;
  border: 1px solid #e2e8f0;
  flex-shrink: 1;
}

.pos-qr-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.35rem;
  margin-top: 0.55rem;
  flex-shrink: 0;
}

.pos-qr-actions .btn {
  display: inline-flex;
  align-items: center;
}

.pos-checkout-point {
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  padding: 0.55rem;
  background: #f8fafc;
}

.pos-point-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.25rem 0;
}

.pos-checkout-extra {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.45rem 0.65rem;
  margin-top: 0;
  min-height: 0;
}

.pos-checkout-extra .form-label {
  margin-bottom: 0.2rem;
}

.pos-cart--checkout .pos-cart__foot {
  padding: 0.75rem 1rem 0.85rem;
}

.pos-cart--checkout .pos-cobro-opts {
  margin-bottom: 0;
}

@media (max-width: 991.98px) {
  .pos-page {
    padding-bottom: calc(3.75rem + env(safe-area-inset-bottom, 0));
  }

  .pos-page--mobile-bar {
    padding-bottom: calc(3.75rem + 3.35rem + env(safe-area-inset-bottom, 0));
  }

  .pos-shell {
    grid-template-columns: 1fr;
    grid-template-rows: minmax(0, 1fr);
    gap: 0.65rem;
    padding: 0.65rem;
    padding-bottom: 0.35rem;
    overflow: hidden;
  }

  .pos-shell--mobile {
    min-height: 0;
  }

  .pos-catalog {
    align-self: stretch;
    flex: 1;
    min-height: 0;
    overflow: hidden;
    padding: 0;
  }

  .pos-cart {
    border-radius: 18px;
    border-left: 0;
    box-shadow: 0 8px 28px rgba(15, 23, 42, 0.1);
  }

  .pos-grid {
    flex: 1;
    min-height: 0;
    height: auto;
    max-height: none;
    grid-template-columns: repeat(auto-fill, minmax(118px, 1fr));
    gap: 0.35rem;
    align-items: start;
    align-content: start;
  }

  .pos-prod {
    min-height: 0;
    padding: 0.4rem 0.5rem;
    border-radius: 10px;
  }

  .pos-prod__name {
    font-size: 0.78rem;
  }

  .pos-scan__input {
    font-size: 1rem;
    min-height: 2.75rem;
    padding-top: 0.45rem;
    padding-bottom: 0.45rem;
  }

  .pos-cats {
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    padding-bottom: 0.15rem;
  }

  .pos-cats::-webkit-scrollbar {
    display: none;
  }

  .pos-cats .btn {
    flex-shrink: 0;
  }

  .pos-cart {
    height: 100%;
    max-height: 100%;
    min-height: 0;
    border-radius: 14px;
  }

  .pos-cart__head {
    padding: 0.75rem 0.85rem 0.4rem;
  }

  .pos-cart__foot {
    padding: 0.65rem 0.85rem calc(0.65rem + env(safe-area-inset-bottom, 0));
  }

  .pos-total__num {
    font-size: 1.45rem;
  }

  .pos-line {
    grid-template-columns: minmax(0, 1fr) minmax(5.25rem, max-content);
    grid-template-areas:
      "info total"
      "qty del";
    column-gap: 0.5rem;
    row-gap: 0.35rem;
    padding: 0.55rem 0.35rem;
  }

  .pos-line__info {
    grid-area: info;
  }

  .pos-line__qty {
    grid-area: qty;
    justify-self: start;
  }

  .pos-line__total {
    grid-area: total;
    align-self: start;
    font-size: 0.9rem;
    width: auto;
    max-width: 100%;
  }

  .pos-line__del {
    grid-area: del;
    align-self: center;
    padding: 0;
    margin-top: 0;
  }

  .pos-checkout-methods {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .pos-checkout-extra {
    grid-template-columns: 1fr;
  }

  .pos-qr-img {
    width: min(240px, 92%);
    max-height: min(240px, calc(100% - 7.25rem));
  }

  .pos-checkout-methods .pos-pay-btn .pos-kbd {
    display: none;
  }

  .pos-mobile-bar {
    position: fixed;
    left: 0;
    right: 0;
    bottom: calc(3.75rem + env(safe-area-inset-bottom, 0));
    z-index: 1035;
    padding: 0.45rem 0.65rem;
    background: rgba(255, 255, 255, 0.98);
    border-top: 1px solid #e2e8f0;
    box-shadow: 0 -6px 20px rgba(15, 23, 42, 0.1);
    backdrop-filter: blur(8px);
  }

  .pos-mobile-bar__btn {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    border: 0;
    border-radius: 999px;
    background: #15803d;
    color: #fff;
    padding: 0.65rem 1rem;
    font-size: 0.88rem;
  }

  .pos-mobile-bar__meta {
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .pos-mobile-bar__action {
    flex-shrink: 0;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    gap: 0.2rem;
  }
}

@media (max-width: 575.98px) {
  .pos-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .pos-checkout-methods {
    grid-template-columns: 1fr 1fr;
  }

  .pos-checkout-extra {
    grid-template-columns: 1fr;
  }

  .pos-qr-img {
    width: min(220px, 88%);
    max-height: min(220px, calc(100% - 7.25rem));
  }

  .pos-cart__back {
    min-width: auto;
  }

  .pos-cart__head-spacer {
    min-width: 0;
    display: none;
  }
}

.pos-checkout-methods .pos-pay-btn {
  position: relative;
}

.pos-checkout-methods .pos-pay-btn .pos-kbd {
  position: absolute;
  top: 0.2rem;
  right: 0.25rem;
  margin-left: 0;
  padding: 0.04rem 0.24rem;
  font-size: 0.55rem;
}

.pos-cliente-dd {
  top: 100%;
  margin-top: 2px;
  z-index: 1080;
  max-height: 220px;
  overflow-y: auto;
  border-radius: 8px;
}

.pos-cart--checkout .pos-cliente-dd {
  top: auto;
  bottom: 100%;
  margin-top: 0;
  margin-bottom: 2px;
  max-height: 160px;
}

.pos-cliente-dd .list-group-item {
  cursor: pointer;
}
</style>
